// pages/AWSInventory.jsx — Dokumentasi AWS: inventory resource multi-region per-service.
// Sumber data: GET /api/aws-inventory (snapshot) & POST /api/aws-inventory/refresh (fetch live).
// EC2: baris bisa diklik → halaman detail terpisah bertab (gaya AWS EC2 Console).
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import useTabDeepLink from '../hooks/useTabDeepLink';
import { api, useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { useDashboardData, formatAWSInventoryDataForAI } from '../context/DashboardDataContext';
import { useAIPage } from '../context/AIPageContext';
import { useActiveDashboard } from '../context/ActiveDashboardContext';
// CronScheduler dipakai bersama dengan tab AWS Billing/Cost & Solman di
// TableMaintenance — satu sumber logika jadwal, jangan diduplikasi di sini.
// useApi() perlu karena CronScheduler memakai gaya fetch (path, {method, body}),
// bukan axios instance `api` di atas.
import { CronScheduler, useApi } from './tmShared';
import AwsTopologyPanel from './AwsTopologyPanel';
import AwsBackupReview, { BackupIcon } from './AwsBackupReview';
import AwsAdvisor, { AdvisorIcon } from './AwsAdvisor';
import CommitmentDetail from './AwsCommitmentsDetail';
import NetworkDetail from './AwsNetworkDetail';
import {
  RefreshCw, Server, HardDrive, Camera, Image as ImageIcon, Key, Network, Globe2,
  Boxes, Database, Bell, Zap, Cloud, Users, ShieldCheck, AlertTriangle,
  Clock, LayoutGrid, Search, X, Tag, ChevronRight, LogIn, LogOut, ChevronsUpDown,
  Menu, ChevronLeft, Plus, Pencil, FolderPlus,
  Activity, MapPin, DollarSign, Trash2, GitCompare, ChevronDown,
  History, FileSpreadsheet, Filter, Share2,
} from 'lucide-react';

// --- Multi-account AWS ---------------------------------------------------
// Akun aktif disimpan di localStorage dan disisipkan otomatis sebagai
// ?account= pada setiap request /api/aws-inventory* lewat interceptor,
// sehingga ~15 pemanggilan api.get/post di halaman ini tidak perlu diubah.
const AWS_ACCOUNT_LS = 'awsinv.account';
let AWS_ACCOUNT = 'primary';
try { AWS_ACCOUNT = localStorage.getItem(AWS_ACCOUNT_LS) || 'primary'; } catch (_) {}
const getAwsAccount = () => AWS_ACCOUNT;
const switchAwsAccount = (key) => {
  AWS_ACCOUNT = key;
  try { localStorage.setItem(AWS_ACCOUNT_LS, key); } catch (_) {}
  window.location.reload();
};
api.interceptors.request.use((cfg) => {
  const u = String(cfg.url || '');
  if (u.startsWith('/api/aws-inventory')) {
    cfg.params = Object.assign({}, cfg.params, { account: AWS_ACCOUNT });
  }
  return cfg;
});

// Referensi tetap untuk service tanpa data: `|| []` membuat array baru tiap render,
// sehingga useMemo kolom di ServiceTable dihitung ulang dan sort ter-reset terus.
const EMPTY_ROWS = [];

const SERVICE_META = {
  // ── Compute ────────────────────────────────────────────────────────────────
  ec2_instances:    { label: 'EC2 Instances',      icon: Server,      global: false, detail: true, groupByBU: true, group: 'Compute',
                      columns: ['region', 'id', 'name', 'type', 'state', 'az', 'privateIp', 'publicIp', 'totalStorageGiB'] },
  service_quota:    { label: 'Service Quotas',     icon: Activity,    global: false, group: 'Compute',
                      columns: ['region', 'name', 'usage', 'limit', 'available', 'usedPct', 'status', 'id'] },
  ami:              { label: 'AMIs',               icon: ImageIcon,   global: false, detail: true,  groupByBU: true, group: 'Compute' },
  // ── Storage ────────────────────────────────────────────────────────────────
  ebs_volumes:      { label: 'EBS Volumes',        icon: HardDrive,   global: false, detail: true, groupByBU: true, group: 'Storage',
                      columns: ['region', 'id', 'name', 'sizeGiB', 'type', 'state', 'az', 'attachedTo'] },
  ebs_snapshots:    { label: 'EBS Snapshots',      icon: Camera,      global: false, groupByBU: true, group: 'Storage',   detail: true,
                      columns: ['region', 'id', 'volumeId', 'sizeGiB', 'state', 'startTime', 'description'] },
  s3:               { label: 'S3 Buckets',         icon: Boxes,       global: true,  groupByBU: true, detail: true, group: 'Storage',
                      columns: ['name', 'region', 'sizeBytes', 'objectCount', 'sizeAt', 'creationDate'],
                      sumColumns: ['sizeBytes', 'objectCount'] },
  // ── Networking ─────────────────────────────────────────────────────────────
  vpc:              { label: 'VPCs',               icon: Network,     global: false, groupByBU: true, group: 'Networking', detail: true,
                      columns: ['region', 'id', 'name', 'cidr', 'state', 'isDefault'] },
  subnet:           { label: 'Subnets',            icon: Network,     global: false, groupByBU: true, group: 'Networking', detail: true,
                      columns: ['region', 'id', 'name', 'vpcId', 'cidr', 'az', 'availableIps'] },
  security_group:   { label: 'Security Groups',    icon: ShieldCheck, global: false, detail: true, groupByBU: true, group: 'Networking',
                      columns: ['region', 'id', 'name', 'vpcId', 'inbound', 'outbound', 'description'] },
  eip:              { label: 'Elastic IPs',        icon: Globe2,      global: false, groupByBU: true, group: 'Networking', detail: true,
                      columns: ['region', 'publicIp', 'name', 'instanceId', 'privateIp', 'associated', 'domain'] },
  key_pairs:        { label: 'Key Pairs',          icon: Key,         global: false, groupByBU: true, group: 'Networking', detail: true,
                      columns: ['region', 'name', 'id', 'type'] },
  // ── VPN ────────────────────────────────────────────────────────────────────
  vpn_connection:   { label: 'VPN Connections',    icon: Network,     global: false, detail: true, groupByBU: true, group: 'VPN',
                      columns: ['region', 'id', 'name', 'state', 'type', 'customerGatewayId', 'vpnGatewayId', 'category'] },
  customer_gateway: { label: 'Customer Gateways',  icon: Globe2,      global: false, detail: true, groupByBU: true, group: 'VPN',
                      columns: ['region', 'id', 'name', 'state', 'type', 'ipAddress', 'bgpAsn', 'deviceName'] },
  vpn_gateway:      { label: 'Virtual Gateways',   icon: ShieldCheck, global: false, detail: true, groupByBU: true, group: 'VPN',
                      columns: ['region', 'id', 'name', 'state', 'type', 'amazonSideAsn', 'availabilityZone'] },
  // ── Serverless & DB ────────────────────────────────────────────────────────
  lambda:           { label: 'Lambda Functions',   icon: Zap,         global: false, groupByBU: true, group: 'Serverless & DB', detail: true,
                      columns: ['region', 'name', 'runtime', 'memoryMB', 'codeSizeKB', 'lastModified'] },
  rds:              { label: 'RDS Instances',      icon: Database,    global: false, groupByBU: true, group: 'Serverless & DB' },
  opensearch:       { label: 'OpenSearch Domains', icon: Database,    global: false, group: 'Serverless & DB', detail: true,
                      columns: ['region', 'name', 'engineVersion', 'state', 'instanceType', 'instanceCount', 'storageGiB', 'access', 'vpcId'] },
  acm_certificate:  { label: 'ACM Certificates',   icon: ShieldCheck, global: false, group: 'Networking', detail: true,
                      columns: ['region', 'domain', 'status', 'type', 'notAfter', 'daysLeft', 'inUse', 'inUseBy'] },
  dlm_policy:       { label: 'DLM Lifecycle Policies', icon: Camera,  global: false, group: 'Backup',
                      columns: ['region', 'name', 'state', 'resourceTypes', 'targetTags', 'interval', 'retainCount', 'policyType'] },
  rds_param_group:  { label: 'RDS Parameter Groups',   icon: Database, global: false, group: 'Serverless & DB',
                      columns: ['region', 'name', 'family', 'description'] },
  ssm_document:     { label: 'SSM Custom Documents',   icon: ShieldCheck, global: false, group: 'Management',
                      columns: ['region', 'name', 'type', 'format', 'version', 'createdAt'] },
  cloudwatch_alarm: { label: 'CloudWatch Alarms',  icon: Bell,        global: false, groupByBU: true, group: 'Monitoring', detail: true,
                      columns: ['region', 'name', 'state', 'metric', 'namespace'] },
  aws_config:       { label: 'AWS Config Rules',   icon: ShieldCheck, global: false, detail: true, group: 'Monitoring',
                      columns: ['region', 'name', 'compliance', 'nonCompliantCount', 'compliantCount', 'scope'] },
  security_hub:     { label: 'Security Hub',       icon: ShieldCheck, global: false, detail: true, group: 'Monitoring',
                      columns: ['region', 'severity', 'title', 'productName', 'resourceType'] },
  // ── Backup ─────────────────────────────────────────────────────────────────
  glacier_vault:    { label: 'Glacier Vaults',     icon: Boxes,       global: false, detail: true, groupByBU: true, group: 'Backup',
                      columns: ['region', 'name', 'numberOfArchives', 'sizeGiB', 'lastInventoryDate'] },
  // ── Jaringan lanjutan (kelompok 2) ────────────────────────────────────────
  internet_gateway: { label: 'Internet Gateways',  icon: Globe2,      global: false, group: 'Networking', detail: true,
    columns: ['region', 'id', 'name', 'vpcId'] },
  nat_gateway:      { label: 'NAT Gateways',       icon: Network,     global: false, group: 'Networking', detail: true,
    columns: ['region', 'id', 'name', 'state', 'connectivityType', 'vpcId', 'subnetId', 'publicIp'] },
  route_table:      { label: 'Route Tables',       icon: Network,     global: false, group: 'Networking', detail: true,
    columns: ['region', 'id', 'name', 'vpcId', 'main', 'exposure', 'defaultRoute', 'routeCount', 'blackholes'] },
  transit_gateway:  { label: 'Transit Gateways',   icon: Network,     global: false, group: 'Networking', detail: true,
    columns: ['region', 'id', 'name', 'state', 'ownerId', 'amazonSideAsn'] },
  vpc_peering:      { label: 'VPC Peering',        icon: Network,     global: false, group: 'Networking', detail: true,
    columns: ['region', 'id', 'name', 'status', 'requesterVpc', 'accepterVpc'] },
  vpc_endpoint:     { label: 'VPC Endpoints',      icon: Network,     global: false, group: 'Networking', detail: true,
    columns: ['region', 'id', 'service', 'type', 'state', 'vpcId', 'privateDns'] },
  network_interface: { label: 'Network Interfaces', icon: Network,    global: false, group: 'Networking', detail: true,
    columns: ['region', 'id', 'name', 'status', 'interfaceType', 'privateIp', 'publicIp', 'attachedTo'] },
  load_balancer:    { label: 'Load Balancers',     icon: Server,      global: false, group: 'Networking', detail: true,
    columns: ['region', 'name', 'type', 'scheme', 'state', 'listeners', 'healthyCount', 'targetCount'] },
  // ── Management & log (kelompok 3) ─────────────────────────────────────────
  ssm_instance:     { label: 'Systems Manager',    icon: ShieldCheck, global: false, group: 'Management', detail: true,
    columns: ['region', 'id', 'name', 'pingStatus', 'platformName', 'agentVersion', 'patchMissing', 'patchCriticalMissing', 'lastPatchTime'] },
  log_group:        { label: 'CloudWatch Log Groups', icon: Database, global: false, group: 'Monitoring', detail: true,
    columns: ['region', 'name', 'retention', 'storedGiB', 'logGroupClass', 'metricFilterCount', 'created'] },
  // ── Cost commitments & budgets ────────────────────────────────────────────
  savings_plans:    { label: 'Savings Plans',      icon: DollarSign,  global: true,  group: 'Cost Commitments', detail: true,
    columns: ['name', 'type', 'paymentOption', 'termMonths', 'end', 'daysLeft', 'utilizationPct', 'state'] },
  ec2_reserved:     { label: 'EC2 Reserved Instances', icon: Server,  global: false, group: 'Cost Commitments', detail: true,
    columns: ['region', 'instanceType', 'instanceCount', 'platform', 'offeringClass', 'end', 'daysLeft', 'utilizationPct', 'state'] },
  rds_reserved:     { label: 'RDS Reserved Instances', icon: Database, global: false, group: 'Cost Commitments', detail: true,
    columns: ['region', 'instanceType', 'instanceCount', 'platform', 'multiAz', 'end', 'daysLeft', 'utilizationPct', 'state'] },
  budgets:          { label: 'Budgets',            icon: Bell,        global: true,  group: 'Cost Commitments', detail: true,
    columns: ['name', 'budgetType', 'timeUnit', 'limit', 'actual', 'forecast', 'forecastPct', 'status'] },
  // ── Global ─────────────────────────────────────────────────────────────────
  iam_users:        { label: 'IAM Users',          icon: Users,       global: true,                   group: 'Global', detail: true,
                      columns: ['name', 'id', 'created', 'arn'] },
  iam_roles:        { label: 'IAM Roles',          icon: ShieldCheck, global: true,                   group: 'Global', detail: true,
                      columns: ['name', 'id', 'created', 'arn'] },
};

const FIELD_LABELS = {
  region: 'Region', id: 'ID', name: 'Name', type: 'Type', state: 'State', status: 'Status',
  az: 'AZ', privateIp: 'Private IP', publicIp: 'Public IP', launchTime: 'Launched',
  totalStorageGiB: 'Storage (GiB)',
  allocationId: 'Allocation ID',
  associated: 'Associated', associationId: 'Association ID', networkInterfaceId: 'Network Interface',
  domain: 'Domain',
  scope: 'Scope', sourceId: 'Rule ID', createdBy: 'Created By', ruleId: 'Rule ID',
  severity: 'Severity', title: 'Title', productName: 'Product', resourceType: 'Resource Type',
  numberOfArchives: 'Archives', sizeGiB: 'Size (GiB)', lastInventoryDate: 'Last Inventory',
  sizeBytes: 'Size', objectCount: 'Objects', sizeAt: 'Size As Of',
  storageGiB: 'Storage (GiB)', attachedTo: 'Attached To',
  encrypted: 'Encrypted', volumeId: 'Volume', startTime: 'Created', description: 'Description',
  creationDate: 'Created', created: 'Created', architecture: 'Arch', cidr: 'CIDR',
  isDefault: 'Default', vpcId: 'VPC', availableIps: 'Free IPs', inbound: 'In', outbound: 'Out',
  instanceId: 'Instance',
  engine: 'Engine', engineVersion: 'Version', class: 'Class', multiAZ: 'Multi-AZ',
  endpoint: 'Endpoint', metric: 'Metric', namespace: 'Namespace', runtime: 'Runtime',
  memoryMB: 'Memory (MB)', codeSizeKB: 'Code (KB)', lastModified: 'Modified', arn: 'ARN',
  paymentOption: 'Payment', termMonths: 'Term (mo)', start: 'Starts', end: 'Ends', daysLeft: 'Days Left',
  utilizationPct: 'Util. 30d (%)', instanceType: 'Instance Type', instanceCount: 'Count', platform: 'Platform',
  offeringClass: 'Class', multiAz: 'Multi-AZ', budgetType: 'Budget Type', timeUnit: 'Period', limit: 'Limit',
  actual: 'Actual', forecast: 'Forecast', actualPct: 'Actual %', forecastPct: 'Forecast %', commitmentHourly: 'Commitment /h',
  connectivityType: 'Connectivity', subnetId: 'Subnet', main: 'Main', exposure: 'Exposure', defaultRoute: 'Default Route',
  routeCount: 'Routes', blackholes: 'Blackholes', ownerId: 'Owner', amazonSideAsn: 'ASN', requesterVpc: 'Requester VPC',
  accepterVpc: 'Accepter VPC', service: 'Service', privateDns: 'Private DNS', interfaceType: 'Interface Type',
  scheme: 'Scheme', listeners: 'Listeners', healthyCount: 'Healthy', targetCount: 'Targets',
  pingStatus: 'SSM Status', platformName: 'OS', agentVersion: 'Agent', patchMissing: 'Missing Patches',
  patchCriticalMissing: 'Critical Missing', lastPatchTime: 'Last Patch Scan', retention: 'Retention',
  storedGiB: 'Stored (GiB)', logGroupClass: 'Class', metricFilterCount: 'Metric Filters',
};

const ISO_RE = /^\d{4}-\d{2}-\d{2}T/;

function fmtCell(val, locale) {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'string' && ISO_RE.test(val)) {
    const d = new Date(val);
    if (!isNaN(d)) return d.toLocaleString(locale, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  }
  return String(val);
}

const STATE_BADGE = {
  running: 'bg-emerald-100 text-emerald-700', available: 'bg-emerald-100 text-emerald-700',
  stopped: 'bg-slate-200 text-slate-600', terminated: 'bg-rose-100 text-rose-700',
  pending: 'bg-amber-100 text-amber-700', stopping: 'bg-amber-100 text-amber-700',
  CRITICAL: 'bg-rose-600 text-white',
  HIGH: 'bg-rose-100 text-rose-700',
  MEDIUM: 'bg-amber-100 text-amber-700',
  LOW: 'bg-brand-100 text-brand-700',
  INFORMATIONAL: 'bg-slate-100 text-slate-500',
  NON_COMPLIANT: 'bg-rose-100 text-rose-700',
  INSUFFICIENT_DATA: 'bg-slate-200 text-slate-500',
  NOT_APPLICABLE: 'bg-slate-100 text-slate-400',
};
const stateBadge = (s) => STATE_BADGE[s] || 'bg-brand-100 text-brand-700';

// ── Panel diff antar-refresh ─────────────────────────────────────────────────
// Sumber: GET /api/aws-inventory/diff (diff terbaru) atau field `diff` pada
// response POST /api/aws-inventory/refresh. Backend sudah menyaring field yang
// berubah sendiri (availableIps subnet, flap alarm, compliance Config) supaya
// yang tampil di sini hanya perubahan nyata.
const KIND_META = {
  added:   { sign: '+', label: 'Added',   bg: '#f0fdf4', bd: '#bbf7d0', fg: '#15803d' },
  removed: { sign: '\u2212', label: 'Removed', bg: '#fef2f2', bd: '#fecaca', fg: '#b91c1c' },
  changed: { sign: '~', label: 'Changed', bg: '#fffbeb', bd: '#fde68a', fg: '#b45309' },
};

function DiffPanel({ diff, loading, onReload, onOpenHistory }) {
  const [open, setOpen] = useState(false);
  const [kindFilter, setKindFilter] = useState('all');

  const HistBtn = () => onOpenHistory ? (
    <button onClick={onOpenHistory}
      className="ml-auto flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100 rounded-lg transition-colors">
      <History style={{ width: 13, height: 13 }} /> History
    </button>
  ) : null;

  if (loading) {
    return (
      <div className="mb-4 px-4 py-3 bg-white border border-slate-200 rounded-xl flex items-center gap-2.5">
        <RefreshCw className="animate-spin text-slate-400" style={{ width: 15, height: 15 }} />
        <span className="text-sm text-slate-500">Loading change log…</span>
      </div>
    );
  }
  if (!diff || diff.empty) return null;

  if (diff.baseline) {
    return (
      <div className="mb-4 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-2.5">
        <GitCompare className="text-slate-400 flex-shrink-0" style={{ width: 16, height: 16, marginTop: 2 }} />
        <p className="text-sm text-slate-600">
          First snapshot recorded — nothing to compare against yet.
          The next refresh will show what changed.
        </p>
        <HistBtn />
      </div>
    );
  }

  const sum = diff.summary || { added: 0, removed: 0, changed: 0, total: 0 };
  const when = diff.prevFetchedAt ? new Date(diff.prevFetchedAt).toLocaleString() : '—';

  if (!sum.total) {
    return (
      <div className="mb-4 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-2.5">
        <GitCompare className="text-emerald-600 flex-shrink-0" style={{ width: 16, height: 16, marginTop: 2 }} />
        <p className="text-sm text-emerald-800">
          No changes since the previous refresh ({when}).
        </p>
        <HistBtn />
      </div>
    );
  }

  const items = (diff.items || []).filter(i => kindFilter === 'all' || i.kind === kindFilter);
  const groups = {};
  items.forEach(i => { (groups[i.service] = groups[i.service] || []).push(i); });

  const Pill = ({ kind, n }) => {
    const m = KIND_META[kind];
    const on = kindFilter === kind;
    return (
      <button
        onClick={() => { setKindFilter(on ? 'all' : kind); setOpen(true); }}
        title={`${m.label}: ${n}`}
        style={{
          background: on ? m.fg : m.bg, borderColor: on ? m.fg : m.bd,
          color: on ? '#fff' : m.fg,
        }}
        className="inline-flex items-center gap-1 px-2.5 py-1 border rounded-lg text-xs font-bold tabular-nums transition-colors">
        {m.sign}{n}
      </button>
    );
  };

  return (
    <div className="mb-4 bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <GitCompare className="text-brand-600 flex-shrink-0" style={{ width: 16, height: 16 }} />
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-semibold text-slate-800">
            {sum.total} change{sum.total === 1 ? '' : 's'} since last refresh
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            Compared against {when}
            {diff.createdBy ? ` · refreshed by ${diff.createdBy}` : ''}
            {diff.truncated ? ' · list truncated' : ''}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <Pill kind="added"   n={sum.added} />
          <Pill kind="removed" n={sum.removed} />
          <Pill kind="changed" n={sum.changed} />
        </div>
        <button onClick={() => setOpen(o => !o)}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
          {open ? 'Hide' : 'Details'}
          <ChevronDown style={{ width: 13, height: 13, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </button>
        {onOpenHistory && (
          <button onClick={onOpenHistory} title="Search the full change log"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100 rounded-lg transition-colors">
            <History style={{ width: 13, height: 13 }} /> History
          </button>
        )}
        {onReload && (
          <button onClick={onReload} title="Reload change log"
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
            <RefreshCw style={{ width: 13, height: 13 }} />
          </button>
        )}
      </div>

      {open && (
        <div className="border-t border-slate-100 max-h-[420px] overflow-y-auto">
          {kindFilter !== 'all' && (
            <div className="px-4 pt-3">
              <button onClick={() => setKindFilter('all')}
                className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:underline">
                <X style={{ width: 12, height: 12 }} /> Clear filter: {KIND_META[kindFilter].label}
              </button>
            </div>
          )}
          {Object.keys(groups).sort().map(svc => (
            <div key={svc} className="px-4 py-3 border-b border-slate-50 last:border-b-0">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                {SERVICE_META[svc]?.label || svc} · {groups[svc].length}
              </p>
              <div className="space-y-1.5">
                {groups[svc].map((it, ix) => {
                  const m = KIND_META[it.kind];
                  return (
                    <div key={it.key + ix} className="flex items-start gap-2.5">
                      <span style={{ background: m.bg, borderColor: m.bd, color: m.fg }}
                        className="flex-shrink-0 w-5 h-5 flex items-center justify-center border rounded text-xs font-bold">
                        {m.sign}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] text-slate-700 break-all">
                          {it.label}
                          <span className="text-slate-400 ml-2 text-[11px]">{it.region}</span>
                        </p>
                        {(it.changes || []).length > 0 && (
                          <ul className="mt-1 space-y-0.5">
                            {it.changes.map((c, ci) => (
                              <li key={ci} className="text-[11.5px] text-slate-500 break-all" style={{ fontFamily: 'ui-monospace, Consolas, monospace' }}>
                                <span className="text-slate-600 font-medium">{c.field}</span>
                                {c.field === '\u2026' ? ` ${c.to}` : <> : {c.from} <span className="text-slate-400">&rarr;</span> {c.to}</>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {!items.length && (
            <p className="px-4 py-6 text-center text-sm text-slate-400">No items for this filter.</p>
          )}
        </div>
      )}
    </div>
  );
}
// ── Penelusuran change log — untuk tracing / audit ───────────────────────────
// Sumber: GET /api/aws-inventory/changes (paginated, difilter di server).
// Filter dikirim sebagai query param, bukan disaring di klien, supaya tetap
// benar walau riwayatnya sudah puluhan ribu baris.
function ChangeLogModal({ open, onClose }) {
  const [filter, setFilter] = useState({ resource: '', service: '', kind: '', from: '', to: '' });
  const [rows, setRows]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [offset, setOffset] = useState(0);
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState(null);
  const [facets, setFacets] = useState(null);
  const [exporting, setExporting] = useState(false);
  const LIMIT = 100;

  const params = useCallback((extra = {}) => {
    const p = { limit: LIMIT, offset, ...extra };
    Object.entries(filter).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filter, offset]);

  const search = useCallback(async (newOffset = null) => {
    if (newOffset !== null) setOffset(newOffset);
    setBusy(true); setErr(null);
    try {
      const p = params(newOffset !== null ? { offset: newOffset } : {});
      const res = await api.get('/api/aws-inventory/changes', { params: p });
      setRows(res.data.changes || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally { setBusy(false); }
  }, [params]);

  useEffect(() => {
    if (!open) return;
    api.get('/api/aws-inventory/changes/facets').then(r => setFacets(r.data)).catch(() => {});
    search(0);
    // eslint-disable-next-line
  }, [open]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const p = {}; Object.entries(filter).forEach(([k, v]) => { if (v) p[k] = v; });
      const res = await api.get('/api/aws-inventory/changes/export', { params: p, responseType: 'blob' });
      if (res.data?.type === 'application/json') {
        throw new Error(JSON.parse(await res.data.text()).error || 'Export gagal.');
      }
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      const url = window.URL.createObjectURL(new Blob([res.data],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = m ? m[1] : `AWS_InventoryChanges_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let msg = e.message;
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).error || msg; } catch (_) {}
      }
      alert('Export gagal: ' + msg);
    } finally { setExporting(false); }
  };

  if (!open) return null;

  const set = (k) => (e) => setFilter(f => ({ ...f, [k]: e.target.value }));
  const inputCls = 'px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)' }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl flex flex-col" style={{ maxHeight: '90vh' }}>

        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
          <History className="text-brand-600" style={{ width: 18, height: 18 }} />
          <div className="flex-1">
            <h3 className="font-display text-base font-bold text-slate-800">Inventory change log</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {facets
                ? `${facets.totalChanges.toLocaleString()} changes recorded${facets.firstChange ? ` since ${facets.firstChange.slice(0, 10)}` : ''} · retained ${facets.retentionDays} days`
                : 'Every added, removed, and changed resource, per refresh.'}
            </p>
          </div>
          <button onClick={exportXlsx} disabled={exporting || !total}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50 rounded-lg transition-colors">
            <FileSpreadsheet style={{ width: 13, height: 13 }} />
            {exporting ? 'Preparing…' : 'Export Excel'}
          </button>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg">
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Resource</label>
            <input value={filter.resource} onChange={set('resource')} placeholder="i-abc123 / web-1"
              onKeyDown={(e) => { if (e.key === 'Enter') search(0); }}
              className={inputCls} style={{ width: 180 }} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Service</label>
            <select value={filter.service} onChange={set('service')} className={inputCls} style={{ width: 165 }}>
              <option value="">All services</option>
              {(facets?.services || []).map(s => (
                <option key={s.service} value={s.service}>
                  {(SERVICE_META[s.service]?.label || s.service)} ({s.count})
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Change</label>
            <select value={filter.kind} onChange={set('kind')} className={inputCls} style={{ width: 110 }}>
              <option value="">All</option>
              <option value="added">Added</option>
              <option value="removed">Removed</option>
              <option value="changed">Changed</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">From</label>
            <input type="date" value={filter.from} onChange={set('from')} className={inputCls} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">To</label>
            <input type="date" value={filter.to} onChange={set('to')} className={inputCls} />
          </div>
          <button onClick={() => search(0)} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:bg-brand-400 rounded-lg transition-colors">
            <Filter style={{ width: 13, height: 13 }} /> Apply
          </button>
          <button onClick={() => { setFilter({ resource: '', service: '', kind: '', from: '', to: '' }); setTimeout(() => search(0), 0); }}
            className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors">
            Reset
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {err && <p className="px-5 py-4 text-sm text-rose-700 bg-rose-50">{err}</p>}
          {busy ? (
            <div className="flex items-center justify-center py-16 text-slate-400 gap-2.5">
              <RefreshCw className="animate-spin" style={{ width: 16, height: 16 }} />
              <span className="text-sm">Searching…</span>
            </div>
          ) : !rows.length ? (
            <p className="py-16 text-center text-sm text-slate-400">No changes match these filters.</p>
          ) : (
            <table className="w-full text-left" style={{ borderCollapse: 'collapse' }}>
              <thead className="sticky top-0 bg-white shadow-sm">
                <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-2.5">Detected</th>
                  <th className="px-3 py-2.5">Change</th>
                  <th className="px-3 py-2.5">Service</th>
                  <th className="px-3 py-2.5">Resource</th>
                  <th className="px-3 py-2.5">Field</th>
                  <th className="px-3 py-2.5">From &rarr; To</th>
                  <th className="px-3 py-2.5">Refreshed by</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const m = KIND_META[r.kind] || KIND_META.changed;
                  return (
                    <tr key={r.id} className="border-t border-slate-50 hover:bg-slate-50/70">
                      <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap" style={{ fontFamily: 'ui-monospace, Consolas, monospace' }}>
                        {r.fetchedAt ? r.fetchedAt.replace('T', ' ').slice(0, 16) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <span style={{ background: m.bg, borderColor: m.bd, color: m.fg }}
                          className="inline-block px-2 py-0.5 border rounded text-[11px] font-bold">
                          {m.sign} {m.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                        {SERVICE_META[r.service]?.label || r.service}
                        <span className="text-slate-400 ml-1.5">{r.region}</span>
                      </td>
                      <td className="px-3 py-2 text-[13px] text-slate-800 break-all" style={{ maxWidth: 240 }}>{r.label}</td>
                      <td className="px-3 py-2 text-xs text-slate-600" style={{ fontFamily: 'ui-monospace, Consolas, monospace' }}>{r.field || '—'}</td>
                      <td className="px-3 py-2 text-[11.5px] text-slate-500 break-all" style={{ maxWidth: 320, fontFamily: 'ui-monospace, Consolas, monospace' }}>
                        {r.kind === 'changed'
                          ? <>{r.from ?? '—'} <span className="text-slate-400">&rarr;</span> {r.to ?? '—'}</>
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{r.createdBy || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex items-center gap-3 px-5 py-3 border-t border-slate-100 bg-slate-50">
          <p className="text-xs text-slate-500 flex-1">
            {total ? `${offset + 1}–${Math.min(offset + LIMIT, total)} of ${total.toLocaleString()}` : '0 results'}
          </p>
          <button onClick={() => search(Math.max(offset - LIMIT, 0))} disabled={busy || offset === 0}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-40 rounded-lg transition-colors">
            <ChevronLeft style={{ width: 13, height: 13 }} /> Prev
          </button>
          <button onClick={() => search(offset + LIMIT)} disabled={busy || offset + LIMIT >= total}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-40 rounded-lg transition-colors">
            Next <ChevronRight style={{ width: 13, height: 13 }} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AWSInventory() {
  const {
    locale,
    t
  } = useLang();
  const id = locale !== 'en-US';
  const [searchParams, setSearchParams] = useSearchParams();
  // Endpoint /api/cron hanya mengizinkan admin (cronAllowed di server.js), jadi
  // panelnya disembunyikan untuk role lain daripada memunculkan 403.
  const { user } = useAuth();
  const cronApi = useApi();
  const [cronOpen, setCronOpen] = useState(false);

  const [snap, setSnap]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRef]  = useState(false);
  const [error, setError]     = useState(null);
  const [active, setActive]   = useState(() => {
    const p = searchParams.get('tab');
    if (p && (p === '__overview__' || p === '__analytics__' || p === '__topology__' || p === '__backup__' || p === '__advisor__' || p === '__tunnel__' || SERVICE_META[p])) return p;
    return '__overview__';
  });
  // Deep link (?tab=) harus lewat jalur yang sama dengan klik sidebar: kalau hanya
  // setActive, detail/drawer, query pencarian, dan returnTo dari tab lama ikut
  // terbawa ke tab baru (drawer bisa merender komponen detail yang salah).
  // Tidak melakukan apa-apa bila tab sudah sama (URL disinkronkan oleh selectService).
  const activeRef = useRef(active);
  activeRef.current = active;
  useTabDeepLink('aws_inventory',
    (p) => p === '__overview__' || p === '__analytics__' || p === '__topology__' || p === '__backup__' || p === '__advisor__' || p === '__tunnel__' || !!SERVICE_META[p],
    (p) => { if (p !== activeRef.current) selectService(p, { syncUrl: false }); });
  const [query, setQuery]     = useState('');
  const [selected, setSelected] = useState(null);
  // Detail dibuka dari tab lain (mis. Topology) → saat detail ditutup, kembali ke tab asal.
  const [returnTo, setReturnTo] = useState(null);
  useEffect(() => { if (!selected && returnTo) { setActive(returnTo); setReturnTo(null); } }, [selected, returnTo]);
  // Identitas stabil: Drawer memasang listener Esc di effect yang bergantung pada
  // onClose — fungsi inline baru tiap render membuat listener dipasang ulang terus.
  const closeDetail = useCallback(() => setSelected(null), []);
  const [groupMode, setGroupMode] = useState({});
  const [s3Create, setS3Create] = useState(false); // dialog "Add S3 Bucket"
  const [drill, setDrill] = useState(null); // { title, rows } drill-through Analytics
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1024); // { [serviceKey]: 'bu'|'type'|'az'|'none' }

  // Diff antar-refresh. Dibaca terpisah dari snapshot supaya tetap ada setelah
  // halaman di-reload, bukan hanya sesaat setelah tombol refresh ditekan.
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(true);
  const [histOpen, setHistOpen] = useState(false);
  const loadDiff = useCallback(async () => {
    setDiffLoading(true);
    try {
      const res = await api.get('/api/aws-inventory/diff');
      setDiff(res.data);
    } catch (e) {
      setDiff(null); // diff gagal tidak perlu mengganggu halaman
    } finally { setDiffLoading(false); }
  }, []);

  // Riwayat resource yang pernah dibuat (change log + CloudTrail) untuk AI.
  const [createdHist, setCreatedHist] = useState(null);
  const loadCreatedHist = useCallback(async () => {
    try { const r = await api.get('/api/aws-inventory/created-history?days=180'); setCreatedHist(r.data); }
    catch (e) { setCreatedHist(null); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/api/aws-inventory');
      setSnap(res.data);
      loadCreatedHist();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); loadDiff(); }, [load, loadDiff]);

  const refresh = useCallback(async () => {
    setRef(true); setError(null);
    try {
      const res = await api.post('/api/aws-inventory/refresh', {}, { timeout: 300000 });
      setSnap(res.data); setSelected(null);
      // Backend menyertakan diff di response refresh — pakai langsung, tanpa
      // request kedua. createdBy diisi manual karena hanya ada di tabel.
      if (res.data?.diff) setDiff({ empty: false, ...res.data.diff });
      else loadDiff();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setRef(false); }
  }, [loadDiff]);

  // Bucket baru langsung tampil di tabel tanpa menunggu refresh penuh dari AWS;
  // snapshot asli akan memuatnya pada refresh berikutnya (cron / tombol Refresh).
  const addS3Bucket = useCallback((bucket) => {
    setSnap((prev) => {
      if (!prev) return prev;
      const inv = prev.inventory || [];
      const idx = inv.findIndex((r) => r.service === 's3');
      if (idx < 0) return prev;
      const row = inv[idx];
      if ((row.data || []).some((b) => b.name === bucket.name)) return prev;
      const next = inv.slice();
      next[idx] = { ...row, data: [...(row.data || []), bucket] };
      return { ...prev, inventory: next };
    });
  }, []);

  const removeS3Bucket = useCallback((name) => {
    setSnap((prev) => {
      if (!prev) return prev;
      return { ...prev, inventory: (prev.inventory || []).map((r) => (r.service === 's3' ? { ...r, data: (r.data || []).filter((b) => b.name !== name) } : r)) };
    });
    setSelected(null);
  }, []);

  const byService = useMemo(() => {
    const map = {};
    (snap?.inventory || []).forEach((row) => {
      if (!map[row.service]) map[row.service] = [];
      const isGlobal = SERVICE_META[row.service]?.global;
      row.data.forEach((item) => {
        const base = isGlobal ? item : { region: row.region, ...item };
        // Inject resource type untuk EditableTags
        if (row.service === 's3') base._rtype = 's3';
        map[row.service].push(base);
      });
    });
    // Flatten security_hub: 1 summary row berisi findings array → jadikan flat list
    if (map['security_hub']) {
      const flat = [];
      map['security_hub'].forEach((row) => {
        if (row._summary && row.findings) flat.push(...row.findings.map((f) => ({ ...f, region: row.region })));
      });
      map['security_hub'] = flat;
      map['_security_hub_summary'] = (map['security_hub_raw'] || map['security_hub']).filter?.((r) => r._summary) || [];
    }
    if (map['ec2_instances'] && map['ebs_volumes']) {
      const volByInst = {};
      map['ebs_volumes'].forEach((v) => {
        (v.attachments || []).forEach((a) => {
          if (a.instanceId) volByInst[a.instanceId] = (volByInst[a.instanceId] || 0) + (v.sizeGiB || 0);
        });
      });
      map['ec2_instances'] = map['ec2_instances'].map((i) => ({
        ...i, totalStorageGiB: volByInst[i.id] ?? 0,
      }));
    }
    return map;
  }, [snap]);

  const volMap = useMemo(() => {
    const m = {}; (byService['ebs_volumes'] || []).forEach((v) => { m[v.id] = v; }); return m;
  }, [byService]);
  const sgMap = useMemo(() => {
    const m = {}; (byService['security_group'] || []).forEach((g) => { m[g.id] = g; }); return m;
  }, [byService]);
  const snapByVol = useMemo(() => {
    const m = {}; (byService['ebs_snapshots'] || []).forEach((s) => { if (s.volumeId) (m[s.volumeId] = m[s.volumeId] || []).push(s); }); return m;
  }, [byService]);
  // AMI ID biasanya ada di deskripsi snapshot (format "Created by CreateImage(...) for ami-xxx")
  const snapByAmi = useMemo(() => {
    const m = {};
    (byService['ebs_snapshots'] || []).forEach((s) => {
      const match = (s.description || '').match(/ami-[a-f0-9]+/i);
      if (match) { const amiId = match[0]; (m[amiId] = m[amiId] || []).push(s); }
    });
    return m;
  }, [byService]);
  const instNameMap = useMemo(() => {
    const m = {}; (byService['ec2_instances'] || []).forEach((i) => { m[i.id] = i.name; }); return m;
  }, [byService]);

  const serviceList = useMemo(() => {
    const order = Object.keys(SERVICE_META);
    return order.filter((k) => (byService[k]?.length || 0) > 0)
      .map((k) => ({ key: k, ...SERVICE_META[k], count: byService[k].length }));
  }, [byService]);

  const meta          = snap?.meta || null;
  const totalRes      = snap?.summary?.totalResources || 0;
  const lastRefreshed = snap?.lastRefreshed
    ? new Date(snap.lastRefreshed).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })
    : null;
  const hasData = serviceList.length > 0;

  // -- Kirim data ke AI Assistant ---------------------------------------------
  const { publishData } = useDashboardData();
  const activeDash = useActiveDashboard();
  const aiPayload = useMemo(() => {
    if (!snap) return null;
    const CREATED_KEYS = ['launchTime','createTime','createdTime','creationDate','createDate','created','startTime','creationTime','CreatedAt','createdAt'];
    const pick = (o, keys) => { for (const k of keys) if (o?.[k]) return o[k]; return null; };
    const allData = [];
    Object.entries(byService).forEach(([svc, items]) => {
      if (svc.startsWith('_') || !Array.isArray(items)) return;
      items.forEach((it) => {
        let tags = null;
        if (it.tags && typeof it.tags === 'object') {
          const e = Array.isArray(it.tags) ? it.tags.map(t => [t.Key ?? t.key, t.Value ?? t.value]) : Object.entries(it.tags);
          tags = e.filter(([k]) => k && k !== 'Name').slice(0, 6).map(([k, v]) => `${k}=${v}`).join(',') || null;
        }
        allData.push({
          service: svc,
          id: it.id || it.arn || it.name || '',
          name: it.name || null,
          region: it.region || null,
          type: it.type || it.instanceType || it.engine || null,
          state: it.state || it.status || null,
          created: pick(it, CREATED_KEYS),
          tags,
        });
      });
    });
    const q = query.trim().toLowerCase();
    const scope = SERVICE_META[active] ? allData.filter(r => r.service === active) : allData;
    const filtered = q ? scope.filter(r => JSON.stringify(r).toLowerCase().includes(q)) : scope;
    return {
      type: 'aws_inventory', allData, filtered,
      summary: snap.summary || null,
      lastRefreshed: snap.lastRefreshed || null,
      diff, createdHist, activeTab: active, query,
      updatedAt: new Date().toISOString(),
    };
  }, [snap, byService, diff, createdHist, active, query]);
  useEffect(() => { if (aiPayload) publishData(aiPayload); }, [aiPayload, publishData]);
  // Page-context ke AI (yang benar-benar ikut dikirim di /api/ai/ask).
  // preferContext: halaman ini juga source SQL, tapi pertanyaan di sini harus
  // dijawab dari snapshot + diff yang tampil, bukan text-to-SQL.
  const { setPageAI } = useAIPage();
  const aiSummary = useMemo(() => (aiPayload ? formatAWSInventoryDataForAI(aiPayload) : null), [aiPayload]);
  useEffect(() => {
    if (!aiSummary) { setPageAI(null); return undefined; }
    // sqlWhen: pertanyaan riwayat/perubahan (resize, kapan, sejak, history) butuh
    // tabel aws_inventory_change via SQL — ringkasan halaman tidak memuatnya.
    setPageAI({ key: 'aws_inventory', label: 'AWS Inventory', summary: aiSummary, preferContext: true,
      sqlWhen: /\b(resiz\w*|riwayat|histor\w*|perubahan|berubah|changed?|changes|kapan|sejak|since|pertumbuhan|growth|grow\w*|tren|trend|ditambah|dihapus|added|removed)\b/i });
  }, [aiSummary, setPageAI]);
  useEffect(() => () => setPageAI(null), [setPageAI]);
  useEffect(() => {
    if (activeDash === 'aws_inventory' && aiPayload) publishData(aiPayload);
  }, [activeDash]); // eslint-disable-line

  // Daftar akun AWS yang terkonfigurasi di backend (tanpa kredensial).
  const [awsAccounts, setAwsAccounts] = useState([]);
  const awsAccount = getAwsAccount();
  useEffect(() => {
    api.get('/api/aws-inventory/accounts')
      .then((r) => setAwsAccounts((r.data && r.data.accounts) || []))
      .catch(() => setAwsAccounts([]));
  }, []);

  // Inventory settings (region scan + sembunyikan resource bawaan AWS) per akun.
  const [invSetOpen, setInvSetOpen] = useState(false);
  const [invSet, setInvSet] = useState(null);
  const [invSetSaving, setInvSetSaving] = useState(false);
  const openInvSettings = async () => {
    setInvSetOpen(true);
    setInvSet(null);
    try {
      const r = await api.get('/api/aws-inventory/settings');
      setInvSet({ regions: r.data.regions || [], hideDefaults: !!r.data.hideDefaults, availableRegions: r.data.availableRegions || [] });
    } catch (_) {
      setInvSet({ regions: [], hideDefaults: false, availableRegions: [] });
    }
  };
  const toggleInvRegion = (rg) => setInvSet((s) => ({
    ...s,
    regions: s.regions.includes(rg) ? s.regions.filter((x) => x !== rg) : s.regions.concat(rg),
  }));
  const saveInvSettings = async () => {
    setInvSetSaving(true);
    try {
      await api.put('/api/aws-inventory/settings', { regions: invSet.regions, hideDefaults: !!invSet.hideDefaults });
      setInvSetOpen(false);
    } catch (e) {
      alert('Failed to save inventory settings.');
    }
    setInvSetSaving(false);
  };

  // Satu-satunya jalur ganti tab: reset semua state milik tab sebelumnya, lalu
  // tulis ?tab= ke URL (replace) supaya URL tidak menyimpan tab lama yang nanti
  // "menarik balik" halaman saat deep-link effect berjalan lagi.
  const selectService = (k, { syncUrl = true } = {}) => {
    setReturnTo(null); setActive(k); setSelected(null); setQuery(''); setS3Create(false); setDrill(null);
    if (syncUrl) {
      setSearchParams((prev) => {
        if (prev.get('tab') === k) return prev;
        const next = new URLSearchParams(prev); next.set('tab', k); return next;
      }, { replace: true });
    }
  };
  // Buka detail resource dari panel lain (Topology / Analytics drill). Tab
  // service ikut diganti, lalu saat detail ditutup kembali ke panel asal.
  const openResourceFrom = (origin, svc, row) => {
    if (!SERVICE_META[svc]?.detail) return;
    setQuery(''); setS3Create(false); setDrill(null);
    setReturnTo(origin); setActive(svc); setSelected(row);
  };

  return (
    <div className="min-h-screen" style={{ background: '#f5f5f0' }}>
    <div className="px-3 py-4 sm:px-4 lg:px-5 max-w-[1920px] mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 text-white"
        style={{ background: "linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)", borderRadius: 14, padding: '16px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.4px' }}>AWS Inventory</h1>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', margin: '3px 0 0' }}>Resource documentation across regions</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-white/80">
          <div className="relative">
            <button
              type="button"
              onClick={() => (invSetOpen ? setInvSetOpen(false) : openInvSettings())}
              className="px-2 py-1 rounded-lg bg-white/15 ring-1 ring-inset ring-white/30 text-white hover:bg-white/25 text-sm font-medium"
              title="Inventory settings"
            >
              Settings
            </button>
            {invSetOpen && (
              <div className="absolute top-full left-0 mt-2 z-50 w-[520px] max-h-[70vh] overflow-auto rounded-xl border border-slate-200 bg-white shadow-xl p-4 text-left text-slate-700">
                <div className="font-semibold text-slate-800 mb-1">Inventory settings</div>
                <div className="text-xs text-slate-500 mb-3">Applies to this AWS account only. Takes effect on the next refresh.</div>
                {!invSet ? (
                  <div className="text-sm text-slate-500">Loading...</div>
                ) : (
                  <div>
                    <label className="flex items-start gap-2 text-sm text-slate-700 mb-4">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={!!invSet.hideDefaults}
                        onChange={(e) => setInvSet({ ...invSet, hideDefaults: e.target.checked })}
                      />
                      <span>
                        Hide AWS-created resources
                        <span className="block text-xs text-slate-500 mt-0.5">
                          Default VPCs with their subnets and default security groups, and IAM service-linked roles.
                        </span>
                      </span>
                    </label>
                    <div className="text-sm font-medium text-slate-700 mb-1">Regions to scan</div>
                    <div className="text-xs text-slate-500 mb-2">
                      Nothing selected means all regions. Global services are always included.
                    </div>
                    <div className="grid grid-cols-3 gap-1 mb-4">
                      {(invSet.availableRegions || []).map((rg) => (
                        <label key={rg} className="flex items-center gap-1.5 text-xs text-slate-700">
                          <input type="checkbox" checked={invSet.regions.includes(rg)} onChange={() => toggleInvRegion(rg)} />
                          {rg}
                        </label>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={saveInvSettings}
                        disabled={invSetSaving}
                        className="px-3 py-1.5 rounded-lg text-white text-sm disabled:opacity-60"
                        style={{ background: 'rgb(var(--brand-600))' }}
                      >
                        {invSetSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setInvSetOpen(false)}
                        className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => setInvSet({ ...invSet, regions: [] })}
                        className="text-xs text-slate-500 underline ml-auto"
                      >
                        Clear region filter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {awsAccounts.length > 1 && (
            <select
              value={awsAccount}
              onChange={(e) => switchAwsAccount(e.target.value)}
              className="px-2 py-1 rounded-lg bg-white/15 ring-1 ring-inset ring-white/30 text-white text-sm font-medium outline-none"
              title="AWS account"
            >
              {awsAccounts.map((a) => (
                <option key={a.key} value={a.key} className="text-slate-800">{a.label}</option>
              ))}
            </select>
          )}
          {meta?.accountId && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/15 rounded-lg font-medium text-white">
              <Cloud style={{ width: 14, height: 14 }} /> {t('awsInv.account')}: {meta.accountId}
            </span>
          )}
          {lastRefreshed && (
            <span className="inline-flex items-center gap-1.5">
              <Clock style={{ width: 14, height: 14 }} /> {t('awsInv.updated')}: {lastRefreshed}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {user?.role === 'admin' && (
            <button onClick={() => setCronOpen((p) => !p)}
              title={t('cron.title')}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-xl border transition-all ${
                cronOpen
                  ? 'bg-slate-800 border-slate-800 text-white'
                  : 'bg-white/15 border-white/30 text-white hover:bg-white/25'
              }`}>
              <Clock style={{ width: 15, height: 15 }} />
              {t('cron.title')}
            </button>
          )}
          <button onClick={refresh} disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-white/90 disabled:opacity-60 text-sm font-semibold rounded-xl shadow-sm transition-all" style={{ color: 'rgb(var(--brand-700))' }}>
            <RefreshCw style={{ width: 15, height: 15 }} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? (t('awsInv.fetching12Min')) : (t('awsInv.refreshAws'))}
          </button>
        </div>
      </div>
      {/* Panel jadwal — dibuka manual, bukan default: yang dibaca sehari-hari
          adalah snapshot & diff, bukan konfigurasi cron. */}
      {user?.role === 'admin' && cronOpen && (
        <CronScheduler api={cronApi} jobKey="aws_inventory"
          runConfirm={t('awsInv.cronConfirm')} />
      )}
      {error && (
        <div className="mb-4 px-4 py-3 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-2.5">
          <AlertTriangle className="text-rose-500 flex-shrink-0" style={{ width: 16, height: 16, marginTop: 2 }} />
          <p className="text-sm text-rose-700 font-medium">{error}</p>
        </div>
      )}
      <DiffPanel diff={diff} loading={diffLoading} onReload={loadDiff}
        onOpenHistory={() => setHistOpen(true)} />
      <ChangeLogModal open={histOpen} onClose={() => setHistOpen(false)} />
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400 gap-3">
          <RefreshCw className="animate-spin" style={{ width: 18, height: 18 }} />
          <span className="text-sm font-medium">{t('awsInv.loading')}</span>
        </div>
      ) : !hasData ? (
        <div className="text-center py-20">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center mb-4">
            <Cloud className="text-brand-500" style={{ width: 30, height: 30 }} />
          </div>
          <h3 className="font-display text-lg font-bold text-slate-800 mb-1">{t('awsInv.noInventoryYet')}</h3>
          <p className="text-sm text-slate-500 mb-5">{t('awsInv.clickRefreshFetchResourcesAws')}</p>
        </div>
      ) : (
        <div className="flex gap-3 items-start relative">
          {/* Toggle button — selalu tampil, posisi sticky */}
          <button onClick={() => setSidebarOpen((p) => !p)}
            title={sidebarOpen ? (t('awsInv.hideSidebar')) : (t('awsInv.showSidebar'))}
            className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center text-slate-500 hover:text-brand-600 transition-all shadow-sm z-20">
            {sidebarOpen
              ? <ChevronLeft style={{ width: 15, height: 15 }} />
              : <Menu        style={{ width: 15, height: 15 }} />}
          </button>

          {/* Overlay mobile (klik tutup sidebar) */}
          {sidebarOpen && (
            <div className="fixed inset-0 bg-black/20 z-10 lg:hidden" onClick={() => setSidebarOpen(false)} />
          )}

          {/* Sidebar */}
          <div className={`
            flex-shrink-0 transition-all duration-200 overflow-hidden
            lg:relative lg:z-auto
            fixed top-0 left-0 h-full z-20 bg-white shadow-xl lg:shadow-none lg:bg-transparent
            ${sidebarOpen ? 'w-60 opacity-100' : 'w-0 opacity-0 pointer-events-none'}
          `}>
            <div className="w-60 pt-2 lg:pt-0 px-2 lg:px-0 overflow-y-auto max-h-[80vh] lg:max-h-none">
            <button onClick={() => selectService('__overview__')}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all mb-1
                ${active === '__overview__' ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-brand-50'}`}>
              <LayoutGrid style={{ width: 16, height: 16 }} /> Overview
            </button>
            <button onClick={() => selectService('__analytics__')}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all mb-1
                ${active === '__analytics__' ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-brand-50'}`}>
              <Activity style={{ width: 16, height: 16 }} /> {t('awsInv.analytics')}
            </button>
            <button onClick={() => selectService('__topology__')}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all mb-1
                ${active === '__topology__' ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-brand-50'}`}>
              <Share2 style={{ width: 16, height: 16 }} /> Topology
            </button>
              <button onClick={() => selectService('__backup__')}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all mb-1
                  ${active === '__backup__' ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-brand-50'}`}>
                <BackupIcon style={{ width: 16, height: 16 }} /> Backup Review
              </button>
              <button onClick={() => selectService('__advisor__')}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all mb-1
                  ${active === '__advisor__' ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-brand-50'}`}>
                <AdvisorIcon style={{ width: 16, height: 16 }} /> Advisor
              </button>
            <button onClick={() => selectService('__tunnel__')}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all mb-1
                  ${active === '__tunnel__' ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-brand-50'}`}>
                <Share2 style={{ width: 16, height: 16 }} /> Network Tunnels
              </button>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mt-3 mb-1.5">Services</div>
            {(() => {
              const groups = {};
              serviceList.forEach((s) => {
                const g = s.group || 'Other';
                if (!groups[g]) groups[g] = [];
                groups[g].push(s);
              });
              return Object.entries(groups).map(([grpName, items]) => (
                <div key={grpName} className="mb-2">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 py-1">{grpName}</div>
                  {items.map((s) => {
                    const Icon = s.icon; const on = active === s.key;
                    return (
                      <button key={s.key} onClick={() => { selectService(s.key); if (window.innerWidth < 1024) setSidebarOpen(false); }}
                        className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-xl text-sm font-medium transition-all mb-0.5
                          ${on ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-brand-50'}`}>
                        <Icon style={{ width: 14, height: 14 }} className="flex-shrink-0" />
                        <span className="flex-1 text-left truncate text-[13px]">{s.label}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-semibold ${on ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>{s.count}</span>
                      </button>
                    );
                  })}
                </div>
              ));
            })()}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            {active === '__overview__'
              ? <OverviewPanel meta={meta} totalRes={totalRes} serviceList={serviceList} id={id} onSelectService={selectService} byService={byService} />
              : active === '__analytics__'
              ? <AnalyticsInventoryPanel byService={byService} summary={snap?.summary} costByService={snap?.costByService} id={id} locale={locale}
                  onDrill={(title, rows) => setDrill({ title, rows })} />
              : active === '__advisor__'
              ? <AwsAdvisor />
              : active === '__backup__'
              ? <AwsBackupReview />
              : active === '__tunnel__'
              ? <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600 space-y-2"><div className="font-semibold text-slate-800">Network Tunnels moved</div><div>Tunnel checks and site device probes now live in Network Topology: <a className="text-brand-600 font-medium underline" href="/dashboard/network-topology?tab=tunnels">Tunnels &amp; WAN</a> · <a className="text-brand-600 font-medium underline" href="/dashboard/network-topology?tab=devices">Site Devices</a> · <a className="text-brand-600 font-medium underline" href="/dashboard/network-topology?tab=overview">Site health overview</a></div></div>
              : active === '__topology__'
              ? <AwsTopologyPanel byService={byService} onRefresh={refresh} refreshing={refreshing} accountId={snap?.meta?.accountId} lastRefreshed={snap?.lastRefreshed}
                  onSelectService={selectService}
                  onOpenResource={(svc, row) => openResourceFrom('__topology__', svc, row)} />
              : (() => {
                  const meta = SERVICE_META[active];
                  const mode = groupMode[active] ?? (meta?.groupByBU ? 'bu' : 'none');
                  const isEc2 = active === 'ec2_instances';
                  const groupOptions = isEc2
                    ? [
                        { value: 'none', label: 'No Grouping' },
                        { value: 'bu',   label: 'Business Unit' },
                        { value: 'type', label: 'Instance Type' },
                        { value: 'az',   label: 'Availability Zone' },
                      ]
                    : meta?.groupByBU
                    ? [{ value: 'none', label: 'No Grouping' }, { value: 'bu', label: 'Business Unit' }]
                    : null;
                  const groupFn = mode === 'none' ? null
                    : mode === 'bu'   ? (r) => (r.tags || []).find((t) => t.key === 'Business Unit')?.value || ''
                    : mode === 'type' ? (r) => r.type || ''
                    : mode === 'az'   ? (r) => r.az || ''
                    : null;
                  const s3Toolbar = active === 's3' && user?.role === 'admin' ? (
                    <button onClick={() => setS3Create(true)}
                      className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-lg px-3 py-1.5 transition-colors">
                      <Plus style={{ width: 13, height: 13 }} /> Add Bucket
                    </button>
                  ) : null;
                  return (
                    <>
                      <ServiceTable key={active} rows={byService[active] || EMPTY_ROWS} meta={meta} query={query} setQuery={setQuery}
                        locale={locale} id={id}
                        activeId={meta?.detail ? selected?.id : null}
                        groupBy={groupFn}
                        groupOptions={groupOptions}
                        groupMode={mode}
                        onGroupChange={(v) => setGroupMode((p) => ({ ...p, [active]: v }))}
                        toolbar={s3Toolbar}
                        onRowClick={meta?.detail ? ((row) => setSelected((p) => (p && p.id === row.id ? null : row))) : null} />
                      {active === 's3' && s3Create && (
                        <S3CreateBucketModal
                          buOptions={[...new Set((byService.s3 || []).map((b) => (b.tags || []).find((t) => t.key === 'Business Unit')?.value).filter(Boolean))].sort()}
                          onClose={() => setS3Create(false)}
                          onCreated={(b) => { addS3Bucket(b); }} />
                      )}
                    </>
                  );
                })()}
          </div>
        </div>
      )}
      {selected && SERVICE_META[active]?.detail && (
        <Drawer onClose={closeDetail}>
          {/* key: detail resource lain = komponen baru. Tanpa ini sub-tab
              (Details/Objects/…) & data yang sudah dimuat dari resource
              sebelumnya ikut terbawa (mis. isi bucket S3 lama). */}
          <div className="p-5" key={`${active}:${selected.id ?? selected.arn ?? selected.name ?? ''}`}>
            {active === 'ec2_instances'    && <Ec2Detail        item={selected} volMap={volMap} sgMap={sgMap} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'ebs_volumes'      && <VolumeDetail     item={selected} snapshots={snapByVol[selected.id] || []} instNameMap={instNameMap} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'security_group'   && <SgDetail         item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 's3'               && <S3Detail         item={selected} onClose={() => setSelected(null)} locale={locale} id={id} onBucketAdded={addS3Bucket} onBucketRemoved={removeS3Bucket} />}
            {active === 'ami'              && <AmiDetail        item={selected} snapshots={snapByAmi[selected.id] || []} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'vpn_connection'   && <VpnConnDetail    item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'customer_gateway' && <CustomerGwDetail item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'vpn_gateway'      && <VpnGwDetail      item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'aws_config'        && <ConfigDetail      item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'security_hub'      && <SecurityHubDetail item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'glacier_vault'     && <GlacierDetail     item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {['savings_plans', 'ec2_reserved', 'rds_reserved', 'budgets'].includes(active) && <CommitmentDetail kind={active} item={selected} onClose={() => setSelected(null)} />}
            {['internet_gateway', 'nat_gateway', 'route_table', 'transit_gateway', 'vpc_peering', 'vpc_endpoint', 'network_interface', 'load_balancer', 'ssm_instance', 'log_group', 'opensearch', 'acm_certificate'].includes(active) && <NetworkDetail kind={active} item={selected} onClose={() => setSelected(null)} />}
            {active === 'eip'               && <EipDetail        item={selected} instNameMap={instNameMap} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'ebs_snapshots'     && <SnapshotDetail   item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'vpc'               && <VpcDetail         item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'subnet'            && <SubnetDetail      item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'key_pairs'         && <KeyPairDetail     item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'lambda'            && <LambdaDetail      item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'cloudwatch_alarm'  && <AlarmDetail       item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'iam_users'         && <IamUserDetail     item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />}
            {active === 'iam_roles'         && <IamRoleDetail     item={selected} onClose={() => setSelected(null)} locale={locale} id={id} />}
          </div>
        </Drawer>
      )}
      {drill && (
        <InventoryAnalyticsDrawer
          title={drill.title} rows={drill.rows} locale={locale} id={id}
          onClose={() => setDrill(null)}
          onOpenResource={(svc, row) => {
            openResourceFrom(active, svc, row);
          }}
        />
      )}
    </div>
    </div>
  );
}

// ── Analytics: Inventory ──────────────────────────────────────────────────────
// Lapisan insight lintas-service dari snapshot inventory. Dihitung penuh di client
// dari `byService` — tanpa endpoint baru, tanpa library chart (bar Tailwind).
// Paket C: hygiene/waste · tag governance · keamanan · cost x-ref · sebaran ·
// state · BU · umur. Klik angka → drawer kanan berisi resource-nya.
const BU_TAG = 'Business Unit';
const ageDays = (s) => { if (!s) return null; const t = new Date(s); return isNaN(t) ? null : (Date.now() - t.getTime()) / 86400000; };
const hasTag = (r, key) => Array.isArray(r.tags) && r.tags.some((t) => t.key === key && t.value);
const buOf = (r) => (Array.isArray(r.tags) ? (r.tags.find((t) => t.key === BU_TAG)?.value || '') : '');

function AnalyticsInventoryPanel({ byService, summary, costByService, id, locale, onDrill }) {
  const T = (a, b) => (id ? a : b);
  const nf = (n) => (n || 0).toLocaleString(locale);
  const drill = (title, rows) => onDrill && onDrill(title, rows);

  // Flat semua resource + anotasi service/group.
  const flat = useMemo(() => {
    const out = [];
    Object.keys(SERVICE_META).forEach((svc) => {
      const meta = SERVICE_META[svc];
      (byService[svc] || []).forEach((r) => out.push({ ...r, _svc: svc, _svcLabel: meta.label, _group: meta.group || 'Other', _groupByBU: !!meta.groupByBU }));
    });
    return out;
  }, [byService]);

  const A = useMemo(() => {
    const tally = (arr, key) => {
      const m = new Map();
      arr.forEach((r) => { const v = key(r); if (v == null || v === '') return; m.set(v, (m.get(v) || 0) + 1); });
      return [...m.entries()].map(([k, v]) => ({ k, v })).sort((a, b) => b.v - a.v);
    };
    // Distribusi
    const bySvc = tally(flat, (r) => r._svcLabel);
    const byRegion = tally(flat, (r) => r.region || 'global');
    const byGroup = tally(flat, (r) => r._group);
    const byState = tally(flat.filter((r) => r.state), (r) => r.state);

    // Hygiene / waste
    const unattachedVol = flat.filter((r) => r._svc === 'ebs_volumes' && r.state === 'available');
    const stoppedEc2 = flat.filter((r) => r._svc === 'ec2_instances' && r.state === 'stopped');
    const unassocEip = flat.filter((r) => r._svc === 'eip' && !r.associated);
    const oldSnap = flat.filter((r) => r._svc === 'ebs_snapshots' && (ageDays(r.startTime) || 0) > 365);
    const oldAmi = flat.filter((r) => r._svc === 'ami' && (ageDays(r.creationDate) || 0) > 365);
    const volGiB = unattachedVol.reduce((s, v) => s + (v.sizeGiB || 0), 0);
    const snapGiB = oldSnap.reduce((s, v) => s + (v.sizeGiB || 0), 0);

    // Tag governance (hanya resource yang punya array tags)
    const taggable = flat.filter((r) => Array.isArray(r.tags));
    const noTags = taggable.filter((r) => r.tags.length === 0);
    const noName = taggable.filter((r) => !hasTag(r, 'Name') && !r.name);
    const noBU = flat.filter((r) => r._groupByBU && Array.isArray(r.tags) && !hasTag(r, BU_TAG));

    // Keamanan
    const openSg = flat.filter((r) => r._svc === 'security_group' && (r.inboundRules || []).some((x) => x.source === '0.0.0.0/0' || x.source === '::/0'));
    const secHub = flat.filter((r) => r._svc === 'security_hub');
    const secBySev = tally(secHub, (r) => r.severity);

    // BU breakdown
    const buTaggable = flat.filter((r) => r._groupByBU && Array.isArray(r.tags));
    const byBU = tally(buTaggable, (r) => buOf(r) || '—');

    // Umur: buckets snapshot & AMI
    const ageBuckets = (arr, field) => {
      const b = { '<90d': [], '90–365d': [], '>1th': [], '?': [] };
      arr.forEach((r) => { const d = ageDays(r[field]); if (d == null) b['?'].push(r); else if (d < 90) b['<90d'].push(r); else if (d <= 365) b['90–365d'].push(r); else b['>1th'].push(r); });
      return b;
    };
    const snapAge = ageBuckets(flat.filter((r) => r._svc === 'ebs_snapshots'), 'startTime');
    const amiAge = ageBuckets(flat.filter((r) => r._svc === 'ami'), 'creationDate');

    // Cost x-ref (defensif — bentuk costByService tak pasti)
    const costRows = (Array.isArray(costByService) ? costByService : [])
      .map((c) => ({ name: c.service || c.ServiceName || c.name || '—', cost: Number(c.cost ?? c.amount ?? c.total ?? c.USD ?? c.Cost ?? 0) }))
      .filter((c) => c.cost > 0).sort((a, b) => b.cost - a.cost).slice(0, 8);

    return {
      total: flat.length, bySvc, byRegion, byGroup, byState,
      unattachedVol, stoppedEc2, unassocEip, oldSnap, oldAmi, volGiB, snapGiB,
      taggable, noTags, noName, noBU,
      openSg, secHub, secBySev,
      byBU, snapAge, amiAge, costRows,
    };
  }, [flat, costByService]);

  const STAT_TONE = {
    blue: 'bg-brand-50 border-brand-100 text-brand-700', amber: 'bg-amber-50 border-amber-100 text-amber-700',
    rose: 'bg-rose-50 border-rose-100 text-rose-700', emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700',
    slate: 'bg-slate-50 border-slate-100 text-slate-700',
  };
  const Card = ({ icon: Icon, title, children, iconClass = 'text-brand-500' }) => (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Icon className={iconClass} style={{ width: 16, height: 16 }} />
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</h3>
      </div>
      {children}
    </div>
  );
  const Bars = ({ items, color = 'bg-brand-500', onClick }) => {
    const m = Math.max(1, ...items.map((x) => x.v));
    return (
      <div className="space-y-1.5">
        {items.map((x) => (
          <button key={x.k} type="button" onClick={onClick ? () => onClick(x.k) : undefined}
            className={`w-full flex items-center gap-3 group ${onClick ? 'cursor-pointer' : 'cursor-default'}`}>
            <div className="w-40 flex-shrink-0 text-xs text-slate-600 truncate text-left group-hover:text-slate-900" title={x.k}>{x.k}</div>
            <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden"><div className={`h-full ${color} rounded`} style={{ width: `${(x.v / m) * 100}%` }} /></div>
            <div className="w-14 flex-shrink-0 text-xs font-semibold text-slate-700 text-right tabular-nums">{nf(x.v)}</div>
          </button>
        ))}
      </div>
    );
  };
  // Kartu temuan hygiene/keamanan.
  const Finding = ({ tone, icon: Icon, label, value, sub, rows, disabled }) => (
    <button type="button" disabled={disabled || !rows.length} onClick={() => drill(label, rows)}
      className={`text-left rounded-2xl p-4 border transition-shadow ${STAT_TONE[tone] || STAT_TONE.slate} ${rows.length ? 'hover:shadow-md cursor-pointer' : 'opacity-60 cursor-default'}`}>
      <div className="flex items-center gap-2 mb-1"><Icon style={{ width: 15, height: 15 }} /><span className="text-xs font-medium">{label}</span></div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-0.5">{sub}</div>}
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Ringkasan */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-2xl p-4 border bg-brand-50 border-brand-100 text-brand-700">
          <div className="text-xs font-medium mb-1">{T('Total resource', 'Total resources')}</div>
          <div className="text-2xl font-bold tabular-nums">{nf(A.total)}</div>
          <div className="text-xs opacity-70 mt-0.5">{A.bySvc.length} {T('jenis', 'types')} · {A.byRegion.length} {T('region', 'regions')}</div>
        </div>
        <Finding tone="amber" icon={Tag} label={T('Tanpa tag', 'Untagged')} value={nf(A.noTags.length)} sub={`${A.taggable.length ? ((A.noTags.length / A.taggable.length) * 100).toFixed(0) : 0}% ${T('dari taggable', 'of taggable')}`} rows={A.noTags} />
        <Finding tone="rose" icon={ShieldCheck} label={T('SG terbuka ke publik', 'World-open SGs')} value={nf(A.openSg.length)} sub="0.0.0.0/0" rows={A.openSg} />
        <Finding tone="rose" icon={AlertTriangle} label={T('Temuan Security Hub', 'Security Hub findings')} value={nf(A.secHub.length)} rows={A.secHub} />
      </div>

      {/* Hygiene / waste */}
      <Card icon={Trash2} title={T('Kandidat hemat biaya (hygiene)', 'Cost-saving candidates (hygiene)')} iconClass="text-amber-500">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Finding tone="amber" icon={HardDrive} label={T('EBS tak terpasang', 'Unattached EBS')} value={nf(A.unattachedVol.length)} sub={`${nf(Math.round(A.volGiB))} GiB`} rows={A.unattachedVol} />
          <Finding tone="amber" icon={Server} label={T('EC2 stopped', 'Stopped EC2')} value={nf(A.stoppedEc2.length)} sub={T('masih bayar EBS', 'still paying EBS')} rows={A.stoppedEc2} />
          <Finding tone="amber" icon={Globe2} label={T('Elastic IP nganggur', 'Idle Elastic IPs')} value={nf(A.unassocEip.length)} sub={T('berbiaya/jam', 'billed hourly')} rows={A.unassocEip} />
          <Finding tone="amber" icon={Camera} label={T('Snapshot >1th', 'Snapshots >1y')} value={nf(A.oldSnap.length)} sub={`${nf(Math.round(A.snapGiB))} GiB`} rows={A.oldSnap} />
          <Finding tone="amber" icon={ImageIcon} label={T('AMI >1th', 'AMIs >1y')} value={nf(A.oldAmi.length)} rows={A.oldAmi} />
        </div>
      </Card>

      {/* Tag governance */}
      <Card icon={Tag} title={T('Tata kelola tag', 'Tag governance')} iconClass="text-amber-500">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Finding tone="amber" icon={Tag} label={T('Tanpa tag sama sekali', 'No tags at all')} value={nf(A.noTags.length)} rows={A.noTags} />
          <Finding tone="amber" icon={Tag} label={T('Tanpa tag Name', 'Missing Name tag')} value={nf(A.noName.length)} rows={A.noName} />
          <Finding tone="amber" icon={Tag} label={T('Tanpa Business Unit', 'Missing Business Unit')} value={nf(A.noBU.length)} rows={A.noBU} />
        </div>
      </Card>

      {/* Keamanan */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card icon={ShieldCheck} title={T('Security Hub per severity', 'Security Hub by severity')} iconClass="text-rose-500">
          {A.secBySev.length ? (
            <div className="space-y-2">
              {A.secBySev.map((s) => (
                <button key={s.k} type="button" onClick={() => drill(`Security Hub — ${s.k}`, A.secHub.filter((r) => r.severity === s.k))}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${stateBadge(s.k)}`}>{s.k}</span>
                  <span className="text-sm font-bold text-slate-700 tabular-nums">{nf(s.v)}</span>
                </button>
              ))}
            </div>
          ) : <div className="text-sm text-emerald-600">{T('Tidak ada temuan.', 'No findings.')}</div>}
        </Card>
        <Card icon={ShieldCheck} title={T('Security group terbuka ke internet', 'Internet-open security groups')} iconClass="text-rose-500">
          {A.openSg.length ? (
            <div className="space-y-1.5">
              {A.openSg.slice(0, 10).map((g) => (
                <button key={g.id} type="button" onClick={() => onDrill(g.name || g.id, [g])}
                  className="w-full flex items-center justify-between text-sm px-2 py-1.5 rounded-lg hover:bg-slate-50 text-left">
                  <span className="truncate text-slate-700">{g.name || g.id} <span className="text-xs text-slate-400">· {g.region}</span></span>
                  <ChevronRight style={{ width: 14, height: 14 }} className="text-slate-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : <div className="text-sm text-emerald-600">{T('Tidak ada SG terbuka ke 0.0.0.0/0.', 'No SGs open to 0.0.0.0/0.')}</div>}
        </Card>
      </div>

      {/* Distribusi */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card icon={Boxes} title={T('Resource per jenis', 'Resources by type')}>
          <Bars items={A.bySvc.slice(0, 12)} color="bg-brand-500" onClick={(k) => drill(k, flat.filter((r) => r._svcLabel === k))} />
        </Card>
        <Card icon={MapPin} title={T('Resource per region', 'Resources by region')} iconClass="text-emerald-500">
          <Bars items={A.byRegion} color="bg-emerald-500" onClick={(k) => drill(k, flat.filter((r) => (r.region || 'global') === k))} />
        </Card>
        <Card icon={LayoutGrid} title={T('Resource per kategori', 'Resources by category')}>
          <Bars items={A.byGroup} color="bg-indigo-500" onClick={(k) => drill(k, flat.filter((r) => r._group === k))} />
        </Card>
        <Card icon={Activity} title={T('Kesehatan state', 'State health')} iconClass="text-amber-500">
          <Bars items={A.byState} color="bg-cyan-500" onClick={(k) => drill(`state: ${k}`, flat.filter((r) => r.state === k))} />
        </Card>
      </div>

      {/* BU breakdown */}
      <Card icon={Users} title={T('Resource per Business Unit', 'Resources by Business Unit')} iconClass="text-indigo-500">
        <Bars items={A.byBU} color="bg-indigo-500"
          onClick={(k) => drill(`BU: ${k}`, flat.filter((r) => r._groupByBU && Array.isArray(r.tags) && (buOf(r) || '—') === k))} />
      </Card>

      {/* Umur snapshot & AMI */}
      <div className="grid md:grid-cols-2 gap-4">
        {[['Snapshot', A.snapAge, 'ebs_snapshots'], ['AMI', A.amiAge, 'ami']].map(([lbl, buckets]) => (
          <Card key={lbl} icon={Clock} title={T(`Umur ${lbl}`, `${lbl} age`)} iconClass="text-slate-500">
            <div className="grid grid-cols-4 gap-2 text-center">
              {['<90d', '90–365d', '>1th', '?'].map((b) => (
                <button key={b} type="button" disabled={!buckets[b].length} onClick={() => drill(`${lbl} · ${b}`, buckets[b])}
                  className={`rounded-lg py-2 ${buckets[b].length ? 'bg-slate-50 hover:bg-slate-100 cursor-pointer' : 'bg-slate-50 opacity-50'}`}>
                  <div className="text-lg font-bold text-slate-700 tabular-nums">{nf(buckets[b].length)}</div>
                  <div className="text-[10px] text-slate-500">{b === '?' ? T('tak diketahui', 'unknown') : b}</div>
                </button>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* Cost x-ref */}
      {A.costRows.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card icon={DollarSign} title={T('Service termahal (biaya)', 'Top services by cost')} iconClass="text-emerald-600">
            <div className="space-y-1.5">
              {A.costRows.map((c) => (
                <div key={c.name} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 truncate">{c.name}</span>
                  <span className="font-semibold text-slate-800 tabular-nums">${c.cost.toLocaleString(locale, { maximumFractionDigits: 0 })}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card icon={Boxes} title={T('Service terbanyak (jumlah)', 'Top services by count')}>
            <Bars items={A.bySvc.slice(0, 8)} color="bg-brand-500" onClick={(k) => drill(k, flat.filter((r) => r._svcLabel === k))} />
          </Card>
        </div>
      )}
    </div>
  );
}

// ── Analytics Drawer: daftar resource generik → klik → detail native service ──
function InventoryAnalyticsDrawer({ title, rows, locale, id, onClose, onOpenResource }) {
  const {
    t
  } = useLang();

  return (
    <Drawer onClose={onClose}>
      <div>
        <div className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">{t('awsInv.resourceDetail')}</div>
            <h2 className="font-display text-lg font-bold text-slate-800 truncate" title={title}>{title}</h2>
            <div className="text-sm text-slate-500">{rows.length.toLocaleString(locale)} {t('awsInv.resources')}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 flex-shrink-0"><X style={{ width: 20, height: 20 }} /></button>
        </div>
        <div className="p-3">
          {rows.length ? rows.map((r, i) => {
            const meta = SERVICE_META[r._svc];
            const clickable = !!meta?.detail;
            const bu = buOf(r);
            return (
              <button key={r.id || r.name || i} type="button" disabled={!clickable}
                onClick={clickable ? () => onOpenResource(r._svc, r) : undefined}
                className={`w-full text-left px-3 py-2.5 rounded-xl border border-transparent transition-all mb-1 ${clickable ? 'hover:bg-white hover:shadow-sm hover:border-slate-100 cursor-pointer' : 'cursor-default'}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-800 truncate">{r.name || r.id || '—'}</span>
                  <span className="flex items-center gap-2 flex-shrink-0">
                    {r.state && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${stateBadge(r.state)}`}>{r.state}</span>}
                    {clickable && <ChevronRight style={{ width: 14, height: 14 }} className="text-slate-400" />}
                  </span>
                </div>
                <div className="text-xs text-slate-500 truncate mt-0.5">
                  {r._svcLabel} · {r.region || 'global'}{r.id && r.name ? <span className="font-mono"> · {r.id}</span> : null}
                  {bu ? <span> · BU: {bu}</span> : null}
                </div>
              </button>
            );
          }) : <div className="text-sm text-slate-400 text-center py-10">{t('awsInv.noResources')}</div>}
        </div>
      </div>
    </Drawer>
  );
}

// ── Drawer geser dari kanan (overlay) ─────────────────────────────────────────
function Drawer({ children, onClose }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setShown(true));
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => { cancelAnimationFrame(r); window.removeEventListener('keydown', onEsc); };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className={`absolute inset-0 bg-slate-900/30 transition-opacity duration-200 ${shown ? 'opacity-100' : 'opacity-0'}`} onClick={onClose} />
      <div className={`relative h-full w-full max-w-2xl bg-slate-50 shadow-2xl overflow-y-auto transition-transform duration-200 ease-out ${shown ? 'translate-x-0' : 'translate-x-full'}`}>
        {children}
      </div>
    </div>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────────
function OverviewPanel({ meta, totalRes, serviceList, id, onSelectService, byService }) {
  const {
    t
  } = useLang();

  const regions = meta?.regionsScanned || [];
  const errors  = meta?.errors || [];
  const [q, setQ] = useState('');

  // Search lintas semua service — flatten semua field string dari setiap item
  const searchResults = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term || term.length < 2) return [];
    const results = [];
    Object.entries(byService || {}).forEach(([svcKey, items]) => {
      if (svcKey.startsWith('_') || !SERVICE_META[svcKey]) return;
      const meta = SERVICE_META[svcKey];
      (items || []).forEach((item) => {
        // Kumpulkan semua nilai string dari item (rekursif 1 level untuk array & object)
        const texts = [];
        const collect = (val) => {
          if (!val) return;
          if (typeof val === 'string') { texts.push(val.toLowerCase()); return; }
          if (typeof val === 'number') { texts.push(String(val)); return; }
          if (Array.isArray(val)) val.forEach((v) => collect(typeof v === 'object' ? Object.values(v || {}) : v));
          else if (typeof val === 'object') Object.values(val).forEach(collect);
        };
        collect(item);
        if (texts.some((t) => t.includes(term))) {
          results.push({ svcKey, svcLabel: meta.label, icon: meta.icon, item });
        }
      });
    });
    return results.slice(0, 50); // max 50 hasil
  }, [q, byService]);

  const Card = ({ icon: Icon, label, value, sub }) => (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
      <div className="flex items-center gap-2 text-slate-400 mb-1.5">
        <Icon style={{ width: 15, height: 15 }} />
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <div className="font-display text-2xl font-bold text-slate-800">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );

  // Label terbaik untuk satu item
  const itemLabel = (item) => item.name || item.title || item.id || '—';

  // Sub-label: region + 1-2 field penting
  const itemSub = (item) => [item.region, item.state, item.type, item.ipAddress, item.cidr]
    .filter(Boolean).slice(0, 3).join(' · ');

  // Highlight keyword dalam teks
  const highlight = (text, term) => {
    if (!text || !term) return text;
    const str = String(text);
    const idx = str.toLowerCase().indexOf(term.toLowerCase());
    if (idx === -1) return str;
    return (
      <>{str.slice(0, idx)}<mark className="bg-yellow-200 text-yellow-900 rounded px-0.5">{str.slice(idx, idx + term.length)}</mark>{str.slice(idx + term.length)}</>
    );
  };

  return (
    <div className="space-y-5">
      {/* Global search */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="relative">
          <Search style={{ width: 16, height: 16 }} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t('awsInv.searchAllResourcesIdName')}
            className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none" />
          {q && (
            <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500">
              <X style={{ width: 14, height: 14 }} />
            </button>
          )}
        </div>

        {/* Hasil search */}
        {q.trim().length >= 2 && (
          <div className="mt-3">
            {searchResults.length === 0 ? (
              <p className="text-[13px] text-slate-400 py-2">{t('awsInv.noResultsFound')}</p>
            ) : (
              <>
                <p className="text-[11px] text-slate-400 font-medium mb-2">
                  {searchResults.length}{searchResults.length === 50 ? '+' : ''} {t('awsInv.results')}
                </p>
                <div className="space-y-1 max-h-[400px] overflow-y-auto">
                  {searchResults.map((r, i) => {
                    const Icon = r.icon;
                    return (
                      <button key={i} onClick={() => onSelectService(r.svcKey)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-brand-50 text-left transition-colors group">
                        <div className="w-7 h-7 rounded-lg bg-slate-100 group-hover:bg-brand-100 flex items-center justify-center flex-shrink-0 transition-colors">
                          <Icon style={{ width: 13, height: 13 }} className="text-slate-500 group-hover:text-brand-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-medium text-slate-700 truncate">
                            {highlight(itemLabel(r.item), q.trim())}
                          </div>
                          <div className="text-[11px] text-slate-400 truncate">
                            <span className="text-brand-500 font-medium">{r.svcLabel}</span>
                            {itemSub(r.item) ? ` · ${itemSub(r.item)}` : ''}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {searchResults.length === 50 && (
                  <p className="text-[11px] text-slate-400 mt-2 text-center">{t('awsInv.resultsCapped50RefineQuery')}</p>
                )}
              </>
            )}
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Card icon={Boxes}      label={t('awsInv.totalResources')} value={totalRes} />
        <Card icon={LayoutGrid} label={t('awsInv.serviceTypes')}    value={serviceList.length} />
        <Card icon={Globe2}     label={t('awsInv.regionsScanned')}  value={regions.length} />
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <h3 className="font-display font-bold text-slate-800 mb-4">{t('awsInv.resourcesPerService')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
          {serviceList.map((s) => {
            const Icon = s.icon;
            return (
              <button key={s.key} onClick={() => onSelectService(s.key)}
                className="flex items-center gap-2.5 px-3 py-2 bg-slate-50 hover:bg-brand-50 hover:border-brand-200 border border-transparent rounded-xl transition-all text-left group">
                <Icon style={{ width: 15, height: 15 }} className="text-slate-400 group-hover:text-brand-500 flex-shrink-0 transition-colors" />
                <span className="text-sm text-slate-600 group-hover:text-brand-600 flex-1 truncate transition-colors">{s.label}</span>
                <span className="text-sm font-bold text-slate-800">{s.count}</span>
              </button>
            );
          })}
        </div>
      </div>
      {errors.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm mb-2">
            <AlertTriangle style={{ width: 15, height: 15 }} />
            {t('awsInv.lengthWarningsDuringFetch', {
              length: errors.length
            })}
          </div>
          <ul className="text-xs text-amber-700/90 space-y-1 max-h-40 overflow-y-auto font-mono">
            {errors.map((e, i) => <li key={i}>• {e}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Tabel per-service ────────────────────────────────────────────────────────
function ServiceTable({ rows, meta, query, setQuery, locale, id, onRowClick, activeId, groupBy, groupOptions, groupMode, onGroupChange, toolbar }) {
  const {
    t
  } = useLang();

  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('asc'); }
  };
  const columns = useMemo(() => {
    if (meta?.columns) return meta.columns;
    const set = new Set();
    rows.forEach((r) => Object.entries(r).forEach(([k, v]) => { if (v === null || typeof v !== 'object') set.add(k); }));
    const all = [...set];
    return all.sort((a, b) => (a === 'region' ? -1 : b === 'region' ? 1 : 0));
  }, [rows, meta]);

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) => columns.some((c) => String(r[c] ?? '').toLowerCase().includes(q)));
  }, [rows, query, columns]);

  // Reset sort ketika pindah service (columns berubah)
  useEffect(() => { setSortCol(null); setSortDir('asc'); }, [columns]);

  const sorted = useMemo(() => {
    if (!sortCol) return filtered;
    return [...filtered].sort((a, b) => {
      const va = a[sortCol] ?? ''; const vb = b[sortCol] ?? '';
      const na = Number(va); const nb = Number(vb);
      let cmp = !isNaN(na) && !isNaN(nb) ? na - nb : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortCol, sortDir]);

  const Icon = meta?.icon || Boxes;
  const colCount = columns.length + (onRowClick ? 1 : 0);

  const renderRow = (r, i) => {
    const isActive = activeId != null && r.id === activeId;
    return (
      <tr key={r.id || i} onClick={onRowClick ? () => onRowClick(r) : undefined}
        className={`border-t border-slate-50 ${onRowClick ? 'cursor-pointer' : ''} ${isActive ? 'bg-brand-50/70' : (onRowClick ? 'hover:bg-brand-50/60' : 'hover:bg-slate-50/60')}`}>
        {columns.map((c) => {
          const isArn = c === 'arn';
          return (
            <td key={c} className={`px-4 py-2 whitespace-nowrap ${isArn ? 'max-w-[220px] truncate font-mono text-[11px]' : ''} ${c === 'name' || c === 'id' ? 'font-medium text-slate-700' : 'text-slate-600'}`}
                title={isArn ? r[c] : undefined}>
              {c === 'state' || c === 'compliance' || c === 'severity'
                ? <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${stateBadge(r[c])}`}>{fmtCell(r[c], locale)}</span>
                : c === 'sizeBytes'
                  // null berarti TIDAK TERUKUR (bucket baru/kosong, atau metrik
                  // CloudWatch belum terbit), bukan nol. Menampilkannya sebagai
                  // "0 B" akan membuat bucket berisi data tampak kosong selama
                  // metriknya belum sampai.
                  ? (r[c] == null
                      ? <span className="text-slate-300" title="No CloudWatch datapoint yet">—</span>
                      : fmtBytes(r[c]))
                  : fmtCell(r[c], locale)}
            </td>
          );
        })}
        {onRowClick && <td className="px-4 py-2 text-slate-300"><ChevronRight style={{ width: 14, height: 14 }} className={isActive ? 'text-brand-500' : ''} /></td>}
      </tr>
    );
  };

  // Subtotal per grup + total keseluruhan untuk kolom numerik yang didaftarkan di
  // meta.sumColumns (mis. S3: sizeBytes, objectCount). Dihitung dari `sorted`,
  // jadi ikut berubah saat pencarian dipakai. Nilai null = tidak terukur → dilewati.
  const sumCols = meta?.sumColumns?.length ? meta.sumColumns : null;
  const sumOf = (rs) => {
    const out = {};
    sumCols.forEach((c) => {
      let total = 0; let have = 0; let missing = 0;
      rs.forEach((r) => {
        const v = r[c];
        if (v == null || v === '' || isNaN(Number(v))) missing += 1; else { total += Number(v); have += 1; }
      });
      out[c] = { total, have, missing };
    });
    return out;
  };
  const fmtSum = (c, s) => {
    if (!s.have) return <span className="text-slate-300">—</span>;
    const txt = c === 'sizeBytes'
      ? (s.total >= 1024 ** 4 ? `${(s.total / 1024 ** 4).toFixed(2)} TB` : fmtBytes(s.total))
      : Number(s.total).toLocaleString(locale);
    return <span title={s.missing ? `${s.missing} row(s) without data are not included` : undefined}>{txt}{s.missing ? '*' : ''}</span>;
  };
  const renderSumRow = (label, rs, key, strong) => {
    const s = sumOf(rs);
    return (
      <tr key={key} className={`border-t border-slate-200 ${strong ? 'bg-slate-100 border-t-2' : 'bg-slate-50/70'} font-semibold text-slate-700`}>
        {columns.map((c, i) => (
          <td key={c} className="px-4 py-2 whitespace-nowrap text-[12px]">
            {i === 0 ? label : (sumCols.includes(c) ? fmtSum(c, s[c]) : '')}
          </td>
        ))}
        {onRowClick && <td />}
      </tr>
    );
  };

  // Pengelompokan (mis. EC2 by Business Unit). Grup tanpa nilai → urut paling akhir.
  const grouped = groupBy
    ? (() => {
        const m = new Map();
        sorted.forEach((r) => { const k = groupBy(r) || ''; if (!m.has(k)) m.set(k, []); m.get(k).push(r); });
        return [...m.entries()].sort((a, b) => (a[0] === '' ? 1 : b[0] === '' ? -1 : a[0].localeCompare(b[0])));
      })()
    : null;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-slate-100">
        <h3 className="font-display font-bold text-slate-800 flex items-center gap-2">
          <Icon style={{ width: 17, height: 17 }} className="text-brand-500" />
          {meta?.label} <span className="text-sm font-medium text-slate-400">({sorted.length})</span>
        </h3>
        <div className="flex items-center gap-2">
          {toolbar}
          {groupOptions && (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Group by</span>
              <select value={groupMode || 'none'} onChange={(e) => onGroupChange(e.target.value)}
                className="text-[12px] font-medium text-slate-600 border border-slate-200 rounded-lg px-2 py-1.5 bg-slate-50 focus:outline-none focus:border-brand-400 cursor-pointer">
                {groupOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}
          <div className="relative">
            <Search style={{ width: 14, height: 14 }} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('awsInv.search')}
              className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none w-44" />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-slate-700 text-slate-200 text-[11px] uppercase tracking-wider">
              {columns.map((c) => {
                const isActive = sortCol === c;
                return (
                  <th key={c} onClick={() => toggleSort(c)}
                    className="text-left font-semibold px-4 py-2.5 whitespace-nowrap cursor-pointer select-none hover:bg-slate-600 transition-colors">
                    <span className="inline-flex items-center gap-1">
                      {FIELD_LABELS[c] || c}
                      {isActive
                        ? (sortDir === 'asc'
                            ? <span className="text-brand-300">▲</span>
                            : <span className="text-brand-300">▼</span>)
                        : <ChevronsUpDown style={{ width: 10, height: 10 }} className="text-slate-500 opacity-60" />}
                    </span>
                  </th>
                );
              })}
              {onRowClick && <th className="px-4 py-2.5" />}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={colCount} className="text-center text-slate-400 py-10 text-sm">{t('awsInv.noResults')}</td></tr>
            ) : grouped ? (
              grouped.map(([g, rs]) => (
                <React.Fragment key={g || '__none__'}>
                  <tr className="border-t-2 border-brand-100 bg-slate-50">
                    <td colSpan={colCount} className="px-4 py-1.5">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">{g || (t('awsInv.noBusinessUnit'))}</span>
                        <span className="text-[10px] font-semibold text-slate-400 bg-slate-200 px-1.5 py-0.5 rounded-full">{rs.length}</span>
                      </span>
                    </td>
                  </tr>
                  {rs.map(renderRow)}
                  {sumCols && renderSumRow(`Subtotal — ${g || t('awsInv.noBusinessUnit')}`, rs, `sub-${g || '__none__'}`, false)}
                </React.Fragment>
              ))
            ) : sorted.map(renderRow)}
            {sumCols && sorted.length > 0 && renderSumRow('Total', sorted, '__total__', true)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Helper detail ─────────────────────────────────────────────────────────────
function KV({ items, locale }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2.5">
      {items.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide mb-0.5">{k}</div>
          <div className="text-[12px] text-slate-700 font-medium break-words">{fmtCell(v, locale)}</div>
        </div>
      ))}
    </div>
  );
}

function MiniTable({ head, rows, empty }) {
  if (!rows.length) return <p className="text-[12px] text-slate-400">{empty}</p>;
  return (
    <div className="overflow-x-auto -m-1 p-1">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-slate-500 text-[10px] uppercase tracking-wider border-b border-slate-100">
            {head.map((h) => <th key={h} className="text-left font-semibold px-3 py-1.5 whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, i) => (
            <tr key={i} className="border-b border-slate-50 last:border-0">
              {cells.map((c, j) => <td key={j} className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Shell detail bertab (header + tab-bar) dipakai semua detail resource ──────
// ── EditableTags: tab Tags yang bisa diedit inline ────────────────────────────
// Props: resourceId, resourceType ('ec2'|'s3'), region, initialTags [{key,value}], id (lang)
function EditableTags({ resourceId, resourceType = 'ec2', region, initialTags = [], id }) {
  const {
    t
  } = useLang();

  const [tags, setTags]       = useState(() => initialTags.map((t, i) => ({ ...t, _id: i })));
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState(null);
  const [editKey, setEditKey] = useState(null); // _id yang sedang diedit
  const nextId                = useRef(initialTags.length);

  const addRow = () => {
    const _id = nextId.current++;
    setTags((p) => [...p, { key: '', value: '', _id, isNew: true }]);
    setEditKey(_id);
  };

  const removeRow = (_id) => setTags((p) => p.filter((t) => t._id !== _id));

  const updateTag = (_id, field, val) =>
    setTags((p) => p.map((t) => t._id === _id ? { ...t, [field]: val } : t));

  const save = async () => {
    const orig = new Map(initialTags.map((t) => [t.key, t.value]));
    const curr = new Map(tags.filter((t) => t.key.trim()).map((t) => [t.key.trim(), t.value]));

    const addTags    = [...curr.entries()]
      .filter(([k, v]) => !orig.has(k) || orig.get(k) !== v)
      .map(([k, v]) => ({ key: k, value: v }));
    const removeTags = [...orig.keys()].filter((k) => !curr.has(k));

    if (!addTags.length && !removeTags.length) { setSaved(true); setTimeout(() => setSaved(false), 2000); return; }

    setSaving(true); setError(null);
    try {
      await api.post('/api/aws-inventory/tags', { resourceId, resourceType, region, addTags, removeTags });
      setSaved(true); setTimeout(() => setSaved(false), 3000);
      setEditKey(null);
      // Update initialTags lokal (reflect changes tanpa refresh global)
      setTags(tags.filter((t) => t.key.trim()).map((t, i) => ({ ...t, _id: i, isNew: false })));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  const isDirty = (() => {
    const orig = new Map(initialTags.map((t) => [t.key, t.value]));
    const curr = new Map(tags.filter((t) => t.key.trim()).map((t) => [t.key.trim(), t.value]));
    if (orig.size !== curr.size) return true;
    for (const [k, v] of curr) if (!orig.has(k) || orig.get(k) !== v) return true;
    for (const k of orig.keys()) if (!curr.has(k)) return true;
    return false;
  })();

  return (
    <div>
      <div className="overflow-x-auto -m-1 p-1 mb-3">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-slate-500 text-[10px] uppercase tracking-wider border-b border-slate-100">
              <th className="text-left font-semibold px-3 py-1.5 w-2/5">Key</th>
              <th className="text-left font-semibold px-3 py-1.5">Value</th>
              <th className="px-2 py-1.5 w-8" />
            </tr>
          </thead>
          <tbody>
            {tags.map((t) => (
              <tr key={t._id} className="border-b border-slate-50 group">
                <td className="px-2 py-1">
                  <input value={t.key} onChange={(e) => updateTag(t._id, 'key', e.target.value)}
                    onFocus={() => setEditKey(t._id)}
                    placeholder="Key"
                    className="w-full text-[12px] px-2 py-1 border border-transparent rounded focus:border-brand-400 focus:bg-white bg-slate-50 outline-none font-medium text-slate-700 transition-colors" />
                </td>
                <td className="px-2 py-1">
                  <input value={t.value} onChange={(e) => updateTag(t._id, 'value', e.target.value)}
                    onFocus={() => setEditKey(t._id)}
                    placeholder="Value"
                    className="w-full text-[12px] px-2 py-1 border border-transparent rounded focus:border-brand-400 focus:bg-white bg-slate-50 outline-none text-slate-600 transition-colors" />
                </td>
                <td className="px-2 py-1 text-center">
                  <button onClick={() => removeRow(t._id)}
                    className="text-slate-300 hover:text-rose-500 transition-colors opacity-0 group-hover:opacity-100">
                    <X style={{ width: 13, height: 13 }} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={addRow}
          className="text-[12px] font-medium text-brand-600 hover:text-brand-700 flex items-center gap-1 transition-colors">
          <span className="text-base leading-none">+</span> {t('awsInv.addTag')}
        </button>
        <div className="flex items-center gap-2">
          {error && <span className="text-[11px] text-rose-600 max-w-[200px] truncate">{error}</span>}
          {saved && !error && <span className="text-[11px] text-emerald-600 font-medium">✓ {t('awsInv.saved')}</span>}
          <button onClick={save} disabled={saving || !isDirty}
            className="text-[12px] font-semibold px-3 py-1.5 rounded-lg transition-all
              bg-brand-600 hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400 text-white">
            {saving ? (t('awsInv.saving')) : (t('awsInv.save'))}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailShell({ icon: Icon, title, idText, badge, badgeClass, sub, tabs, onClose, id }) {
  const {
    t
  } = useLang();

  const [tab, setTab] = useState(tabs[0].key);
  const activated = useRef(new Set([tabs[0].key]));

  const switchTab = (key) => {
    setTab(key);
    if (!activated.current.has(key)) {
      activated.current.add(key);
      const t = tabs.find((t) => t.key === key);
      if (t?.onActivate) t.onActivate();
    }
  };
  const cur = tabs.find((t) => t.key === tab) || tabs[0];
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center flex-shrink-0">
            <Icon style={{ width: 20, height: 20 }} className="text-brand-500" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display text-lg font-bold text-slate-800 leading-tight truncate">{title}</h2>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {idText && <span className="text-sm text-slate-400 font-mono">{idText}</span>}
              {badge && <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${badgeClass}`}>{badge}</span>}
              {sub && <span className="text-xs text-slate-400">{sub}</span>}
            </div>
          </div>
        </div>
        <button onClick={onClose} title={t('awsInv.close')}
          className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg p-1.5 flex-shrink-0 transition-colors">
          <X style={{ width: 18, height: 18 }} />
        </button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex items-center px-3 border-b border-slate-100 overflow-x-auto">
          {tabs.map((tb) => {
            const TIcon = tb.icon; const on = tab === tb.key;
            return (
              <button key={tb.key} onClick={() => switchTab(tb.key)}
                className={`relative flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors
                  ${on ? 'text-brand-600' : 'text-slate-500 hover:text-slate-700'}`}>
                <TIcon style={{ width: 15, height: 15 }} /> {tb.label}
                {on && <span className="absolute left-3 right-3 bottom-0 h-0.5 bg-brand-600 rounded-full" />}
              </button>
            );
          })}
        </div>
        <div className="p-5">{cur.render()}</div>
      </div>
    </div>
  );
}

// ── Detail EC2 ────────────────────────────────────────────────────────────────
function Ec2Detail({ item, volMap, sgMap, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v);
  const summary = [
    ['Instance ID', item.id], ['Name', item.name || '—'], ['Instance type', item.type],
    ['State', item.state], ['AMI', item.ami], ['Platform', item.platform],
    ['Architecture', item.architecture], ['Region', item.region], ['Availability Zone', item.az],
    ['Tenancy', item.tenancy], ['Launch time', item.launchTime], ['Key pair', item.keyName],
    ['IAM role', item.iamRole], ['Monitoring', item.monitoring], ['EBS optimized', item.ebsOptimized],
    ['vCPU', item.cpuCores != null ? `${item.cpuCores} core × ${item.cpuThreadsPerCore} thread` : null],
    ['Root device', item.rootDeviceName ? `${item.rootDeviceName} (${item.rootDeviceType})` : null],
    ['State reason', item.stateReason],
  ];
  const networking = [
    ['Public IPv4', item.publicIp], ['Private IPv4', item.privateIp],
    ['Public DNS', item.publicDns], ['Private DNS', item.privateDns],
    ['VPC', item.vpcId], ['Subnet', item.subnetId],
  ];
  const storageRows = (item.blockDevices || []).map((b) => {
    const v = b.volumeId ? volMap[b.volumeId] : null;
    return [dash(b.device), dash(b.volumeId), v?.sizeGiB != null ? `${v.sizeGiB} GiB` : '—',
      dash(v?.type), v ? (v.encrypted ? 'Yes' : 'No') : '—', b.deleteOnTermination ? 'Yes' : 'No', dash(b.status)];
  });
  const sgRows = (item.securityGroups || []).map((g) => {
    const s = sgMap[g.id];
    return [dash(g.id), dash(g.name), s ? `${s.inbound} in / ${s.outbound} out` : '—', dash(s?.description)];
  });
  const niRows  = (item.networkInterfaces || []).map((n) => [dash(n.id), dash(n.privateIp), dash(n.subnetId), dash(n.description)]);
  const tagRows = (item.tags || []).map((t) => [t.key, t.value]);
  const storageTotalGiB = (item.blockDevices || []).reduce((a, b) => {
    const v = b.volumeId ? volMap[b.volumeId] : null; return a + (v?.sizeGiB || 0);
  }, 0);

  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: Server, render: () => <KV items={summary} locale={locale} /> },
    { key: 'security', label: 'Security', icon: ShieldCheck, render: () =>
      <MiniTable head={['Group ID', 'Name', 'Rules', 'Description']} rows={sgRows} empty={t('awsInv.noSecurityGroups')} /> },
    { key: 'networking', label: 'Networking', icon: Network, render: () => (
      <>
        <KV items={networking} locale={locale} />
        {niRows.length > 0 && (
          <div className="mt-5 pt-5 border-t border-slate-50">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Network Interfaces</div>
            <MiniTable head={['Interface', 'Private IP', 'Subnet', 'Description']} rows={niRows} empty="—" />
          </div>
        )}
      </>
    ) },
    { key: 'storage', label: 'Storage', icon: HardDrive, render: () => (
      <div>
        <div className="flex items-center gap-3 mb-3 text-sm flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 rounded-lg font-medium text-slate-600">
            <HardDrive style={{ width: 14, height: 14 }} className="text-slate-400" />
            {item.blockDevices?.length || 0} {t('awsInv.volumes')}
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-50 rounded-lg font-semibold text-brand-600">
            Total: {storageTotalGiB} GiB
          </span>
        </div>
        <MiniTable head={['Device', 'Volume', 'Size', 'Type', 'Encrypted', 'Delete on Term.', 'Status']}
          rows={storageRows} empty={t('awsInv.noVolumes')} />
      </div>
    ) },
    { key: 'iamrole', label: 'IAM Role', icon: ShieldCheck, render: () =>
      <InstanceRolePanel instanceId={item.id} region={item.region} /> },
    { key: 'tags', label: 'Tags', icon: Tag, render: () =>
      <EditableTags resourceId={item.id} resourceType="ec2" region={item.region} initialTags={item.tags || []} id={id} /> },
  ];

  return <DetailShell icon={Server} title={item.name || item.id} idText={item.id}
    badge={item.state} badgeClass={stateBadge(item.state)} sub={`${item.type} · ${item.region}`}
    tabs={tabs} onClose={onClose} id={id} />;
}

// ── IAM role policies: view, add/edit/delete inline policy (shared) ─────────────
const S3_ACTIONS = {
  read:  ['s3:GetObject'],
  write: ['s3:PutObject', 's3:AbortMultipartUpload', 's3:ListMultipartUploadParts'],
  rw:    ['s3:GetObject', 's3:PutObject', 's3:AbortMultipartUpload', 's3:ListMultipartUploadParts'],
};
function buildS3Policy(bucket, prefix, mode) {
  const b = bucket.trim(); const p = (prefix || '').trim().replace(/^\/+/, '');
  return {
    Version: '2012-10-17',
    Statement: [
      { Sid: 'ListBucket', Effect: 'Allow', Action: ['s3:ListBucket', 's3:GetBucketLocation'], Resource: `arn:aws:s3:::${b}` },
      { Sid: 'ObjectAccess', Effect: 'Allow', Action: S3_ACTIONS[mode], Resource: `arn:aws:s3:::${b}/${p ? p.replace(/\/?$/, '/') : ''}*` },
    ],
  };
}
const polInputCls = 'w-full text-[12px] border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-brand-400';
const polBtn = 'inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50';

function RolePolicyBlock({ roleName, roleArn, attached = [], inline = [], onChanged, showHeader = true }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [editor, setEditor] = useState(null); // { name, text, isNew }
  const [tpl, setTpl] = useState({ bucket: '', prefix: '', mode: 'write' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [openDoc, setOpenDoc] = useState({});

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      JSON.parse(editor.text);
      await api.post(`/api/aws-inventory/iam/role/${encodeURIComponent(roleName)}/inline-policy`,
        { policyName: editor.name.trim(), document: editor.text });
      setMsg({ ok: true, text: `Policy "${editor.name}" saved on role ${roleName}. It takes effect within 1–2 minutes.` });
      setEditor(null); onChanged && onChanged();
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.error || e.message }); }
    setBusy(false);
  };
  const remove = async (name) => {
    if (!window.confirm(`Delete inline policy "${name}" from role ${roleName}?`)) return;
    setBusy(true); setMsg(null);
    try {
      await api.post(`/api/aws-inventory/iam/role/${encodeURIComponent(roleName)}/inline-policy/delete`, { policyName: name });
      setMsg({ ok: true, text: `Policy "${name}" deleted.` }); onChanged && onChanged();
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.error || e.message }); }
    setBusy(false);
  };
  const applyTemplate = () => {
    if (!tpl.bucket.trim()) return;
    setEditor((ed) => ({
      ...ed,
      name: ed.name || `s3-${tpl.mode}-${tpl.bucket.trim()}`.slice(0, 128),
      text: JSON.stringify(buildS3Policy(tpl.bucket, tpl.prefix, tpl.mode), null, 2),
    }));
  };
  const addBtn = isAdmin && (
    <button disabled={busy} onClick={() => setEditor({ name: '', text: '', isNew: true })}
      className={polBtn + ' bg-brand-600 text-white hover:bg-brand-700'}><Plus style={{ width: 13, height: 13 }} />Add inline policy</button>
  );

  return (
    <div className="space-y-3">
      {msg && <div className={`text-[12px] rounded-lg px-3 py-2 ${msg.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'}`}>{msg.text}</div>}
      <div className={showHeader ? 'border border-slate-100 rounded-xl p-3 space-y-2' : 'space-y-2'}>
        <div className="flex items-center gap-2">
          {showHeader ? <>
            <ShieldCheck style={{ width: 14, height: 14 }} className="text-brand-500" />
            <span className="text-[13px] font-semibold text-slate-700 flex-1 truncate" title={roleArn}>Role: {roleName}</span>
          </> : <span className="flex-1" />}
          {addBtn}
        </div>
        {attached.map((p) => (
          <div key={p.arn || p.name} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
            <span className="text-[12px] font-medium text-slate-700 flex-1 truncate" title={p.arn}>{p.name}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-100 text-brand-600 font-semibold">Managed</span>
          </div>
        ))}
        {inline.map((p) => (
          <div key={p.name} className="bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2 px-3 py-2">
              <button className="text-[12px] font-medium text-slate-700 flex-1 truncate text-left hover:underline"
                onClick={() => setOpenDoc((o) => ({ ...o, [p.name]: !o[p.name] }))}>{p.name}</button>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 font-semibold">Inline</span>
              {isAdmin && <>
                <button disabled={busy} className="text-[11px] text-brand-600 font-semibold hover:underline"
                  onClick={() => setEditor({ name: p.name, text: JSON.stringify(p.document || {}, null, 2), isNew: false })}>Edit</button>
                <button disabled={busy} title="Delete" className="text-rose-500 hover:text-rose-700" onClick={() => remove(p.name)}><Trash2 style={{ width: 13, height: 13 }} /></button>
              </>}
            </div>
            {openDoc[p.name] && <pre className="text-[11px] text-slate-600 px-3 pb-3 overflow-x-auto">{JSON.stringify(p.document, null, 2)}</pre>}
          </div>
        ))}
        {!attached.length && !inline.length && <p className="text-[12px] text-slate-400">No policies on this role.</p>}
      </div>

      {editor && (
        <div className="border border-brand-200 rounded-xl p-3 space-y-3 bg-brand-50/30">
          <div className="flex items-center">
            <span className="text-[13px] font-semibold text-slate-700 flex-1">{editor.isNew ? 'Add' : 'Edit'} inline policy · role {roleName}</span>
            <button onClick={() => setEditor(null)} className="text-slate-400 hover:text-slate-600"><X style={{ width: 14, height: 14 }} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
            <div className="sm:col-span-2"><label className="text-[11px] text-slate-500">S3 bucket (template)</label>
              <input className={polInputCls} placeholder="bucket-name" value={tpl.bucket} onChange={(e) => setTpl({ ...tpl, bucket: e.target.value })} /></div>
            <div><label className="text-[11px] text-slate-500">Prefix (optional)</label>
              <input className={polInputCls} placeholder="folder/" value={tpl.prefix} onChange={(e) => setTpl({ ...tpl, prefix: e.target.value })} /></div>
            <div><label className="text-[11px] text-slate-500">Access</label>
              <select className={polInputCls} value={tpl.mode} onChange={(e) => setTpl({ ...tpl, mode: e.target.value })}>
                <option value="write">Write (upload)</option><option value="read">Read</option><option value="rw">Read + write</option>
              </select></div>
          </div>
          <button onClick={applyTemplate} disabled={!tpl.bucket.trim()} className={polBtn + ' bg-white border border-brand-300 text-brand-700 hover:bg-brand-50'}>Generate S3 policy</button>
          <div><label className="text-[11px] text-slate-500">Policy name</label>
            <input className={polInputCls} disabled={!editor.isNew} value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} /></div>
          <div><label className="text-[11px] text-slate-500">Policy document (JSON)</label>
            <textarea rows={12} className={polInputCls + ' font-mono'} value={editor.text} onChange={(e) => setEditor({ ...editor, text: e.target.value })} /></div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditor(null)} className={polBtn + ' bg-slate-100 text-slate-600'}>Cancel</button>
            <button disabled={busy || !editor.name.trim() || !editor.text.trim()} onClick={save} className={polBtn + ' bg-brand-600 text-white hover:bg-brand-700'}>{busy ? 'Saving…' : 'Save policy'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── EC2 → IAM role (instance profile) ──────────────────────────────────────────
function InstanceRolePanel({ instanceId, region }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [state, setState] = useState('loading');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [profilePick, setProfilePick] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/api/aws-inventory/ec2/${encodeURIComponent(instanceId)}/iam-role`, { params: { region } });
      setData(r.data); setState('done');
    } catch (e) { setErr(e.response?.data?.error || e.message); setState('error'); }
  }, [instanceId, region]);
  useEffect(() => { setState('loading'); load(); }, [load]);

  const attachProfile = async () => {
    if (!profilePick) return;
    if (!window.confirm(`Attach instance profile "${profilePick}" to ${instanceId}?`)) return;
    setBusy(true); setMsg(null);
    try {
      await api.post(`/api/aws-inventory/ec2/${encodeURIComponent(instanceId)}/iam-role`, { region, profileName: profilePick });
      setMsg({ ok: true, text: `Instance profile "${profilePick}" attached.` }); load();
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.error || e.message }); }
    setBusy(false);
  };

  if (state === 'loading') return <div className="flex items-center gap-2 text-slate-400 py-4"><RefreshCw className="animate-spin" style={{ width: 14, height: 14 }} /><span className="text-[12px]">Loading…</span></div>;
  if (state === 'error') return <div className="text-[12px] text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{err}</div>;

  return (
    <div className="space-y-4">
      {msg && <div className={`text-[12px] rounded-lg px-3 py-2 ${msg.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'}`}>{msg.text}</div>}
      {!data.profile ? (
        <div className="space-y-2">
          <div className="text-[12px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2">This instance has no IAM role (instance profile) attached.</div>
          {isAdmin && (
            <div className="flex gap-2 items-center flex-wrap">
              <select className={polInputCls + ' max-w-xs'} value={profilePick} onChange={(e) => setProfilePick(e.target.value)}>
                <option value="">Select instance profile…</option>
                {data.availableProfiles.map((p) => <option key={p.name} value={p.name}>{p.name}{p.roles.length ? ` (role: ${p.roles.join(', ')})` : ''}</option>)}
              </select>
              <button disabled={busy || !profilePick} onClick={attachProfile} className={polBtn + ' bg-brand-600 text-white hover:bg-brand-700'}><Plus style={{ width: 13, height: 13 }} />Attach</button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="text-[12px] text-slate-500">Instance profile: <span className="font-semibold text-slate-700">{data.profile.name}</span></div>
          {data.roles.map((r) => (
            <RolePolicyBlock key={r.name} roleName={r.name} roleArn={r.arn}
              attached={r.attachedPolicies} inline={r.inlinePolicies} onChanged={load} />
          ))}
        </>
      )}
    </div>
  );
}

// ── Detail EBS Volume ─────────────────────────────────────────────────────────
function VolumeDetail({ item, snapshots = [], instNameMap = {}, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const details = [
    ['Volume ID', item.id], ['Name', item.name || '—'], ['Size', item.sizeGiB != null ? `${item.sizeGiB} GiB` : null],
    ['Type', item.type], ['IOPS', item.iops], ['Throughput', item.throughput != null ? `${item.throughput} MB/s` : null],
    ['State', item.state], ['Encrypted', item.encrypted], ['Availability Zone', item.az],
    ['Region', item.region], ['Created', item.createTime], ['Snapshot', item.snapshotId],
    ['Multi-Attach', item.multiAttach],
  ];
  const fmtTime = (t) => (t ? new Date(t).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
  const attRows = (item.attachments || []).map((a) => [
    a.instanceId || '—', instNameMap[a.instanceId] || '—', a.device || '—', a.state || '—',
    a.deleteOnTermination ? 'Yes' : 'No', fmtTime(a.attachTime),
  ]);
  const snapRows = (snapshots || []).map((s) => [
    s.id, s.sizeGiB != null ? `${s.sizeGiB} GiB` : '—', s.state, fmtTime(s.startTime), s.description || '—',
  ]);
  const tagRows = (item.tags || []).map((t) => [t.key, t.value]);

  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: HardDrive, render: () => <KV items={details} locale={locale} /> },
    { key: 'attachments', label: t('awsInv.attachments'), icon: Server, render: () =>
      <MiniTable head={['Instance ID', 'Instance Name', 'Device', 'State', 'Delete on Term.', 'Attached']} rows={attRows} empty={t('awsInv.notAttached')} /> },
    { key: 'snapshots', label: 'Snapshots', icon: Camera, render: () =>
      <MiniTable head={['Snapshot ID', 'Size', 'State', 'Started', 'Description']} rows={snapRows} empty={t('awsInv.noRelatedSnapshots')} /> },
    { key: 'tags', label: 'Tags', icon: Tag, render: () =>
      <EditableTags resourceId={item.id} resourceType="ec2" region={item.region} initialTags={item.tags || []} id={id} /> },
  ];

  return <DetailShell icon={HardDrive} title={item.name || item.id} idText={item.id}
    badge={item.state} badgeClass={stateBadge(item.state)} sub={`${item.sizeGiB} GiB · ${item.type} · ${item.region}`}
    tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail Security Group ─────────────────────────────────────────────────────
function SgDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const [rulesState, setRulesState] = useState('idle');
  const [rules, setRules]           = useState(null);
  const [rulesErr, setRulesErr]     = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Form tambah rule + status mutasi
  const [showAdd, setShowAdd]   = useState(false);
  const [form, setForm]         = useState({ preset: 'custom', ipProtocol: 'tcp', fromPort: '', toPort: '', source: '', description: '' });
  const [busy, setBusy]         = useState(false);
  const [mutErr, setMutErr]     = useState(null);

  const fetchRules = useCallback(async () => {
    setRulesState('loading'); setRulesErr(null);
    try {
      const res = await api.get(`/api/aws-inventory/sg/${encodeURIComponent(item.id)}?region=${item.region || 'ap-southeast-1'}`);
      setRules(res.data); setRulesState('done');
    } catch (e) {
      setRulesErr(e.response?.data?.error || e.message); setRulesState('error');
    }
  }, [item.id, item.region]);

  // Preset port umum
  const PRESETS = {
    custom: { label: 'Custom', proto: 'tcp', from: '', to: '' },
    ssh:    { label: 'SSH (22)', proto: 'tcp', from: 22, to: 22 },
    http:   { label: 'HTTP (80)', proto: 'tcp', from: 80, to: 80 },
    https:  { label: 'HTTPS (443)', proto: 'tcp', from: 443, to: 443 },
    rdp:    { label: 'RDP (3389)', proto: 'tcp', from: 3389, to: 3389 },
    all:    { label: 'All traffic', proto: '-1', from: '', to: '' },
  };

  const applyPreset = (key) => {
    const p = PRESETS[key];
    setForm((f) => ({ ...f, preset: key, ipProtocol: p.proto, fromPort: p.from === '' ? '' : String(p.from), toPort: p.to === '' ? '' : String(p.to) }));
  };

  const detectKind = (src) => {
    if (/^sg-/.test(src)) return 'sg';
    if (/^pl-/.test(src)) return 'prefix';
    if (src.includes(':')) return 'cidr6';
    return 'cidr4';
  };

  const submitAdd = async () => {
    if (!form.source.trim()) { setMutErr(t('awsInv.sourceRequired')); return; }
    setBusy(true); setMutErr(null);
    const rule = {
      ipProtocol: form.ipProtocol,
      fromPort: form.ipProtocol === '-1' ? null : (form.fromPort === '' ? null : Number(form.fromPort)),
      toPort:   form.ipProtocol === '-1' ? null : (form.toPort === '' ? Number(form.fromPort) : Number(form.toPort)),
      source: form.source.trim(),
      kind: detectKind(form.source.trim()),
      description: form.description.trim() || undefined,
    };
    try {
      const res = await api.post(`/api/aws-inventory/sg/${encodeURIComponent(item.id)}/inbound`,
        { region: item.region || 'ap-southeast-1', action: 'add', rule });
      setRules(res.data); setShowAdd(false);
      setForm({ preset: 'custom', ipProtocol: 'tcp', fromPort: '', toPort: '', source: '', description: '' });
    } catch (e) { setMutErr(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  const removeRule = async (r) => {
    if (!window.confirm(t('awsInv.removeInboundRuleProtocolPorts', {
      protocol: r.protocol,
      ports: r.ports,
      source: r.source
    }))) return;
    setBusy(true); setMutErr(null);
    try {
      const res = await api.post(`/api/aws-inventory/sg/${encodeURIComponent(item.id)}/inbound`,
        { region: item.region || 'ap-southeast-1', action: 'remove', rule: { ...r.raw, description: r.description } });
      setRules(res.data);
    } catch (e) { setMutErr(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  const details = [
    ['Group ID', item.id], ['Name', item.name || '—'], ['VPC', item.vpcId], ['Region', item.region],
    ['Description', item.description], ['Inbound rules', item.inbound], ['Outbound rules', item.outbound],
  ];

  const RulesPanel = ({ type }) => {
    const {
      t
    } = useLang();

    const editable = type === 'in' && isAdmin;
    const ruleList = rules ? (type === 'in' ? rules.inboundRules : rules.outboundRules) : [];
    const emptyMsg = type === 'in'
      ? (t('awsInv.noInboundRules'))
      : (t('awsInv.noOutboundRules'));
    if (rulesState === 'idle')    return <p className="text-[12px] text-slate-400">{t('awsInv.clickTabLoadRules')}</p>;
    if (rulesState === 'loading') return <div className="flex items-center gap-2 text-slate-400 py-3"><RefreshCw className="animate-spin" style={{ width: 14, height: 14 }} /><span className="text-[12px]">Loading…</span></div>;
    if (rulesState === 'error')   return <div className="text-[12px] text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{rulesErr}</div>;

    return (
      <div>
        <div className="overflow-x-auto -m-1 p-1">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-slate-500 text-[10px] uppercase tracking-wider border-b border-slate-100">
                <th className="text-left font-semibold px-3 py-1.5">Protocol</th>
                <th className="text-left font-semibold px-3 py-1.5">Port</th>
                <th className="text-left font-semibold px-3 py-1.5">{type === 'in' ? 'Source' : 'Destination'}</th>
                <th className="text-left font-semibold px-3 py-1.5">Description</th>
                {editable && <th className="px-2 py-1.5 w-8" />}
              </tr>
            </thead>
            <tbody>
              {ruleList.length === 0 ? (
                <tr><td colSpan={editable ? 5 : 4} className="px-3 py-4 text-center text-slate-400 text-[12px]">{emptyMsg}</td></tr>
              ) : ruleList.map((r, i) => (
                <tr key={i} className="border-b border-slate-50 group">
                  <td className="px-3 py-1.5 text-slate-700">{r.protocol}</td>
                  <td className="px-3 py-1.5 text-slate-600">{r.ports}</td>
                  <td className="px-3 py-1.5 text-slate-600 font-mono text-[11px]">{r.source}</td>
                  <td className="px-3 py-1.5 text-slate-500">{r.description || '—'}</td>
                  {editable && (
                    <td className="px-2 py-1.5 text-center">
                      <button onClick={() => removeRule(r)} disabled={busy}
                        className="text-slate-300 hover:text-rose-500 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-30">
                        <X style={{ width: 13, height: 13 }} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {mutErr && <div className="mt-2 text-[11px] text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{mutErr}</div>}
        {editable && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            {!showAdd ? (
              <button onClick={() => { setShowAdd(true); setMutErr(null); }}
                className="text-[12px] font-medium text-brand-600 hover:text-brand-700 flex items-center gap-1">
                <span className="text-base leading-none">+</span> {t('awsInv.addInboundRule')}
              </button>
            ) : (
              <div className="bg-slate-50 rounded-xl p-3 space-y-2.5">
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(PRESETS).map(([k, p]) => (
                    <button key={k} onClick={() => applyPreset(k)}
                      className={`text-[11px] px-2 py-1 rounded-md font-medium transition-colors ${form.preset === k ? 'bg-brand-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-brand-300'}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-[10px] text-slate-400 font-semibold uppercase">Protocol</label>
                    <select value={form.ipProtocol} onChange={(e) => setForm((f) => ({ ...f, ipProtocol: e.target.value, preset: 'custom' }))}
                      className="w-full text-[12px] px-2 py-1.5 border border-slate-200 rounded-lg bg-white outline-none focus:border-brand-400">
                      <option value="tcp">TCP</option><option value="udp">UDP</option>
                      <option value="icmp">ICMP</option><option value="-1">All</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-400 font-semibold uppercase">From Port</label>
                    <input value={form.fromPort} onChange={(e) => setForm((f) => ({ ...f, fromPort: e.target.value, preset: 'custom' }))}
                      disabled={form.ipProtocol === '-1'} placeholder="80"
                      className="w-full text-[12px] px-2 py-1.5 border border-slate-200 rounded-lg bg-white outline-none focus:border-brand-400 disabled:bg-slate-100" />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-400 font-semibold uppercase">To Port</label>
                    <input value={form.toPort} onChange={(e) => setForm((f) => ({ ...f, toPort: e.target.value, preset: 'custom' }))}
                      disabled={form.ipProtocol === '-1'} placeholder="80"
                      className="w-full text-[12px] px-2 py-1.5 border border-slate-200 rounded-lg bg-white outline-none focus:border-brand-400 disabled:bg-slate-100" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-slate-400 font-semibold uppercase">Source (CIDR / sg-id)</label>
                    <input value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
                      placeholder="0.0.0.0/0"
                      className="w-full text-[12px] px-2 py-1.5 border border-slate-200 rounded-lg bg-white outline-none focus:border-brand-400 font-mono" />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-400 font-semibold uppercase">Description</label>
                    <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder={t('awsInv.optional')}
                      className="w-full text-[12px] px-2 py-1.5 border border-slate-200 rounded-lg bg-white outline-none focus:border-brand-400" />
                  </div>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <button onClick={() => { setShowAdd(false); setMutErr(null); }}
                    className="text-[12px] font-medium text-slate-500 px-3 py-1.5">{t('awsInv.cancel')}</button>
                  <button onClick={submitAdd} disabled={busy}
                    className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white">
                    {busy ? (t('awsInv.adding')) : (t('awsInv.addRule'))}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    { key: 'details',  label: t('awsInv.details'), icon: ShieldCheck, render: () => <KV items={details} locale={locale} /> },
    { key: 'inbound',  label: 'Inbound',  icon: LogIn,  onActivate: fetchRules, render: () => <RulesPanel type="in" /> },
    { key: 'outbound', label: 'Outbound', icon: LogOut, onActivate: fetchRules, render: () => <RulesPanel type="out" /> },
    { key: 'tags',     label: 'Tags',     icon: Tag,    render: () => <EditableTags resourceId={item.id || item.name} resourceType={item._rtype || 'ec2'} region={item.region || 'ap-southeast-1'} initialTags={item.tags || []} id={id} /> },
  ];

  return <DetailShell icon={ShieldCheck} title={item.name || item.id} idText={item.id}
    sub={`${item.vpcId || ''} · ${item.region}`} tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail S3 Bucket — tab Objects fetch on-demand ────────────────────────────
const fmtBytes = (b) => {
  if (b == null) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

function S3Detail({ item, onClose, locale, id, onBucketAdded, onBucketRemoved }) {
  const {
    t
  } = useLang();

  const [objState, setObjState] = useState('idle'); // idle | loading | done | error
  const [objData,  setObjData]  = useState(null);
  const [objErr,   setObjErr]   = useState(null);
  const [prefix,   setPrefix]   = useState('');    // navigasi folder

  const fetchObjects = useCallback(async (pfx = '', force = false) => {
    setObjState('loading'); setObjErr(null);
    try {
      const params = new URLSearchParams({ region: item.region || 'us-east-1' });
      if (pfx) params.set('prefix', pfx);
      if (force) params.set('force', '1');
      const res = await api.get(`/api/aws-inventory/s3/${encodeURIComponent(item.name)}?${params}`);
      setObjData(res.data); setPrefix(pfx); setObjState('done');
    } catch (e) {
      setObjErr(e.response?.data?.error || e.message); setObjState('error');
    }
  }, [item.name, item.region]);

  // ── Folder management (admin only; backend enforces it as well) ──────────────
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renaming, setRenaming] = useState(null); // { prefix, value }
  const [opBusy, setOpBusy] = useState(false);
  const [opErr, setOpErr] = useState(null);
  const [opNotice, setOpNotice] = useState(null);

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { setOpErr('Folder name is required.'); return; }
    setOpBusy(true); setOpErr(null); setOpNotice(null);
    try {
      await api.post(`/api/aws-inventory/s3/${encodeURIComponent(item.name)}/folder`,
        { region: item.region || 'us-east-1', prefix, name });
      setNewFolderOpen(false); setNewFolderName('');
      setOpNotice(`Folder "${name}" created.`);
      await fetchObjects(prefix);
    } catch (e) {
      setOpErr(e.response?.data?.error || e.message);
    } finally { setOpBusy(false); }
  };

  const renameFolder = async () => {
    const newName = (renaming?.value || '').trim();
    if (!newName) { setOpErr('Folder name is required.'); return; }
    const oldName = renaming.prefix.replace(/\/$/, '').split('/').pop();
    if (newName === oldName) { setRenaming(null); return; }
    if (!window.confirm(`Rename folder "${oldName}" to "${newName}"?\n\nS3 has no native rename: every object in the folder is copied to the new name and the originals are then deleted.`)) return;
    setOpBusy(true); setOpErr(null); setOpNotice(null);
    try {
      const r = await api.post(`/api/aws-inventory/s3/${encodeURIComponent(item.name)}/folder/rename`,
        { region: item.region || 'us-east-1', prefix: renaming.prefix, newName }, { timeout: 300000 });
      setRenaming(null);
      const n = r.data.moved;
      setOpNotice(`Folder renamed to "${newName}" (${n} object${n === 1 ? '' : 's'} moved).${(r.data.warnings || []).length ? ' ' + r.data.warnings.join(' ') : ''}`);
      await fetchObjects(prefix);
    } catch (e) {
      setOpErr(e.response?.data?.error || e.message);
    } finally { setOpBusy(false); }
  };

  const details = [
    ['Bucket Name', item.name], ['Region', item.region], ['Created', item.creationDate],
  ];
  const tagRows = (item.tags || []).map((t) => [t.key, t.value]);

  // Breadcrumb dari prefix aktif
  const crumbs = prefix ? prefix.split('/').filter(Boolean) : [];

  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: Boxes, render: () => (
      <div>
        <KV items={details} locale={locale} />
        {isAdmin && <S3RenameBucketPanel item={item} onAdded={onBucketAdded} onRemoved={onBucketRemoved} />}
      </div>
    ) },
    {
      key: 'objects', label: 'Objects', icon: HardDrive,
      onActivate: () => { if (objState === 'idle') fetchObjects(''); },
      render: () => (
        <div>
          {/* Breadcrumb navigasi */}
          <div className="flex items-center gap-1 flex-wrap text-[12px] mb-3 text-slate-500">
            <button onClick={() => fetchObjects('')} className="hover:text-brand-600 font-medium transition-colors">
              {item.name}
            </button>
            {crumbs.map((c, i) => {
              const pfx = crumbs.slice(0, i + 1).join('/') + '/';
              return (
                <React.Fragment key={pfx}>
                  <span className="text-slate-300">/</span>
                  <button onClick={() => fetchObjects(pfx)} className="hover:text-brand-600 font-medium transition-colors">{c}</button>
                </React.Fragment>
              );
            })}
          </div>

          {isAdmin && (
            <div className="mb-3">
              <button onClick={() => { setNewFolderOpen((v) => !v); setOpErr(null); }} disabled={opBusy}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-brand-600 bg-brand-50 hover:bg-brand-100 rounded-lg px-2.5 py-1 transition-colors disabled:opacity-50">
                <FolderPlus style={{ width: 13, height: 13 }} /> New Folder
              </button>
              {newFolderOpen && (
                <div className="flex items-center gap-2 mt-2">
                  <input autoFocus value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') setNewFolderOpen(false); }}
                    placeholder={prefix ? `Folder name (inside ${prefix})` : 'Folder name'}
                    className="flex-1 min-w-0 text-[12px] px-2.5 py-1.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-brand-400 outline-none" />
                  <button onClick={createFolder} disabled={opBusy}
                    className="text-[12px] font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-lg px-3 py-1.5 disabled:opacity-50">Create</button>
                  <button onClick={() => setNewFolderOpen(false)} disabled={opBusy}
                    className="text-[12px] font-medium text-slate-500 hover:text-slate-700 px-2 py-1.5">Cancel</button>
                </div>
              )}
            </div>
          )}
          {opBusy && (
            <div className="flex items-center gap-2 text-slate-500 text-[12px] bg-slate-50 rounded-lg px-3 py-2 mb-3">
              <RefreshCw className="animate-spin" style={{ width: 13, height: 13 }} />
              {renaming ? 'Renaming folder… this can take a while for large folders.' : 'Working…'}
            </div>
          )}
          {opErr && <div className="text-[12px] text-rose-600 bg-rose-50 rounded-lg px-3 py-2 mb-3">{opErr}</div>}
          {opNotice && <div className="text-[12px] text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 mb-3">{opNotice}</div>}

          {objState === 'idle' && (
            <p className="text-[12px] text-slate-400">{t('awsInv.clickObjectsTabLoad')}</p>
          )}
          {objState === 'loading' && (
            <div className="flex items-center gap-2 text-slate-400 py-4">
              <RefreshCw className="animate-spin" style={{ width: 14, height: 14 }} />
              <span className="text-[12px]">{t('awsInv.fetchingBucketContents')}</span>
            </div>
          )}
          {objState === 'error' && (
            <div className="text-[12px] text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{objErr}</div>
          )}
          {objState === 'done' && objData && (
            <>
              {/* Ringkasan */}
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 rounded-lg text-[12px] font-medium text-slate-600">
                  📁 {objData.totalFolders} {t('awsInv.folders')}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 rounded-lg text-[12px] font-medium text-slate-600">
                  📄 {objData.totalObjects} {t('awsInv.files')}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-brand-50 rounded-lg text-[12px] font-semibold text-brand-600">
                  {fmtBytes(objData.totalSizeAll ?? objData.totalSize)}
                </span>
                <span className="ml-auto inline-flex items-center gap-2 text-[11px] text-slate-400">
                  {objData.fetchedAt && (
                    <span title={objData.cached ? 'From cache (auto-refresh every Monday 02:00)' : 'Fetched live from AWS'}>
                      Updated {new Date(objData.fetchedAt).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })}
                    </span>
                  )}
                  <button onClick={() => fetchObjects(prefix, true)} title="Fetch again from AWS"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 text-slate-500 hover:text-brand-600 hover:bg-brand-50">
                    <RefreshCw style={{ width: 12, height: 12 }} /> Refresh
                  </button>
                </span>
              </div>
              {objData.truncated && (
                <div className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-1.5 mb-2">
                  This folder holds more than 300,000 objects. Folder sizes are partial.
                </div>
              )}

              {/* Tabel folder + file */}
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-slate-500 text-[10px] uppercase tracking-wider border-b border-slate-100">
                      <th className="text-left font-semibold px-2 py-1.5">Name</th>
                      <th className="text-left font-semibold px-2 py-1.5">Size</th>
                      <th className="text-left font-semibold px-2 py-1.5">Last Modified</th>
                      <th className="text-left font-semibold px-2 py-1.5">Storage Class</th>
                      {isAdmin && <th className="px-2 py-1.5 w-8" />}
                    </tr>
                  </thead>
                  <tbody>
                    {objData.prefixes.map((p) => {
                      const label = p.prefix.slice(prefix.length);
                      const isRen = renaming?.prefix === p.prefix;
                      return (
                        <tr key={p.prefix} className="border-b border-slate-50 hover:bg-brand-50/40 cursor-pointer"
                          onClick={isRen ? undefined : () => fetchObjects(p.prefix)}>
                          <td className="px-2 py-1.5 text-brand-600 font-medium" onClick={isRen ? (e) => e.stopPropagation() : undefined}>
                            {isRen ? (
                              <span className="inline-flex items-center gap-1.5">
                                📁
                                <input autoFocus value={renaming.value} onChange={(e) => setRenaming((r) => ({ ...r, value: e.target.value }))}
                                  onKeyDown={(e) => { if (e.key === 'Enter') renameFolder(); if (e.key === 'Escape' && !opBusy) setRenaming(null); }}
                                  disabled={opBusy}
                                  className="text-[12px] px-2 py-1 border border-slate-200 rounded-md bg-white focus:border-brand-400 outline-none text-slate-700 font-normal w-40" />
                                <button onClick={renameFolder} disabled={opBusy}
                                  className="text-[11px] font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-md px-2 py-1 disabled:opacity-50">Save</button>
                                <button onClick={() => setRenaming(null)} disabled={opBusy}
                                  className="text-[11px] font-medium text-slate-500 hover:text-slate-700 px-1.5 py-1">Cancel</button>
                              </span>
                            ) : <>📁 {label}</>}
                          </td>
                          <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap" title={p.objectCount != null ? `${p.objectCount} objects` : undefined}>
                            {p.size != null ? fmtBytes(p.size) : '—'}
                          </td>
                          <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">
                            {p.lastModified ? new Date(p.lastModified).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                          </td>
                          <td className="px-2 py-1.5 text-slate-400">—</td>
                          {isAdmin && (
                            <td className="px-2 py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                              {!isRen && (
                                <button title="Rename folder" disabled={opBusy}
                                  onClick={() => { setOpErr(null); setOpNotice(null); setRenaming({ prefix: p.prefix, value: label.replace(/\/$/, '') }); }}
                                  className="p-1 rounded-md text-slate-400 hover:text-brand-600 hover:bg-brand-50 disabled:opacity-40">
                                  <Pencil style={{ width: 13, height: 13 }} />
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {objData.objects.filter((o) => o.key !== prefix).map((o) => {
                      const label = o.key.slice(prefix.length);
                      return (
                        <tr key={o.key} className="border-b border-slate-50 hover:bg-slate-50/60">
                          <td className="px-2 py-1.5 text-slate-700">📄 {label}</td>
                          <td className="px-2 py-1.5 text-slate-500">{fmtBytes(o.size)}</td>
                          <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">
                            {o.lastModified ? new Date(o.lastModified).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                          </td>
                          <td className="px-2 py-1.5 text-slate-400">{o.storageClass || '—'}</td>
                          {isAdmin && <td />}
                        </tr>
                      );
                    })}
                    {objData.prefixes.length === 0 && objData.objects.length === 0 && (
                      <tr><td colSpan={isAdmin ? 5 : 4} className="px-2 py-4 text-center text-slate-400">{t('awsInv.bucketEmpty')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      ),
    },
    { key: 'tags', label: 'Tags', icon: Tag, render: () =>
      <EditableTags resourceId={item.name} resourceType="s3" region={item.region} initialTags={item.tags || []} id={id} /> },
  ];

  return <DetailShell icon={Boxes} title={item.name} idText={item.region}
    sub={item.creationDate ? new Date(item.creationDate).toLocaleDateString(locale) : null}
    tabs={tabs} onClose={onClose} id={id} />;
}

// ── Panel: Rename S3 bucket (admin only) ─────────────────────────────────────
// S3 tidak bisa me-rename bucket: bucket baru dibuat, objek + tag disalin, lalu
// bucket lama dihapus HANYA lewat konfirmasi terpisah.
function S3RenameBucketPanel({ item, onAdded, onRemoved }) {
  const [phase, setPhase] = useState('idle'); // idle | form | busy | done | deleting
  const [newName, setNewName] = useState('');
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  const [confirmName, setConfirmName] = useState('');

  const nameOk = /^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/.test(newName) && !newName.includes('..') && newName !== item.name;

  const start = async () => {
    if (!nameOk) { setErr('Enter a valid new bucket name (3-63 characters: lowercase letters, numbers, dots and hyphens).'); return; }
    if (!window.confirm(`Create bucket "${newName}" and copy all objects from "${item.name}"?\n\nThe old bucket is kept until you delete it separately.`)) return;
    setPhase('busy'); setErr(null);
    try {
      const r = await api.post(`/api/aws-inventory/s3/${encodeURIComponent(item.name)}/rename`,
        { region: item.region, newName }, { timeout: 600000 });
      setResult(r.data);
      onAdded && onAdded(r.data.bucket);
      setPhase('done');
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
      setPhase('form');
    }
  };

  const deleteOld = async () => {
    setPhase('deleting'); setErr(null);
    try {
      await api.post(`/api/aws-inventory/s3/${encodeURIComponent(item.name)}/delete`,
        { region: item.region, confirmName, replacedBy: result.newName }, { timeout: 600000 });
      onRemoved && onRemoved(item.name);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
      setPhase('done');
    }
  };

  return (
    <div className="mt-5 pt-4 border-t border-slate-100">
      <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide mb-2">Rename bucket</div>

      {phase === 'idle' && (
        <button onClick={() => setPhase('form')}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-brand-600 bg-brand-50 hover:bg-brand-100 rounded-lg px-2.5 py-1 transition-colors">
          <Pencil style={{ width: 13, height: 13 }} /> Rename bucket…
        </button>
      )}

      {(phase === 'form' || phase === 'busy') && (
        <div className="space-y-2.5">
          <p className="text-[12px] text-slate-500">
            S3 cannot rename a bucket. A new bucket is created in the same region ({item.region}), objects and tags are copied and verified,
            and <span className="font-semibold">the old bucket is kept</span> until you delete it. Limits: up to 5,000 objects and 10 GB,
            no archived (Glacier) objects, versioning off. Policies, lifecycle rules and other settings are not copied.
          </p>
          <div className="flex items-center gap-2">
            <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value.trim())} disabled={phase === 'busy'}
              onKeyDown={(e) => { if (e.key === 'Enter' && phase === 'form') start(); }}
              placeholder="New bucket name"
              className="flex-1 min-w-0 text-[12px] px-2.5 py-1.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-brand-400 outline-none font-mono" />
            <button onClick={start} disabled={phase === 'busy' || !newName}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-lg px-3 py-1.5 disabled:opacity-50">
              {phase === 'busy' && <RefreshCw className="animate-spin" style={{ width: 12, height: 12 }} />} Rename
            </button>
            <button onClick={() => { setPhase('idle'); setErr(null); }} disabled={phase === 'busy'}
              className="text-[12px] font-medium text-slate-500 hover:text-slate-700 px-2 py-1.5">Cancel</button>
          </div>
          {phase === 'busy' && <p className="text-[12px] text-slate-500">Copying objects… keep this window open.</p>}
        </div>
      )}

      {(phase === 'done' || phase === 'deleting') && result && (
        <div className="space-y-3">
          <div className="text-[12px] text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
            Bucket <span className="font-mono font-semibold">{result.newName}</span> created with {result.moved} object{result.moved === 1 ? '' : 's'} ({fmtBytes(result.totalSize)}), verified identical to the original. The old bucket <span className="font-mono">{item.name}</span> was kept.
          </div>
          {(result.warnings || []).map((w, i) => <div key={i} className="text-[12px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2">{w}</div>)}
          <div className="rounded-lg border border-rose-100 p-3 space-y-2">
            <div className="text-[12px] text-slate-600">
              Done with the old bucket? Update anything that still points to <span className="font-mono">{item.name}</span> first, then type its name to delete it permanently.
            </div>
            <div className="flex items-center gap-2">
              <input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} disabled={phase === 'deleting'}
                placeholder={item.name}
                className="flex-1 min-w-0 text-[12px] px-2.5 py-1.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-rose-400 outline-none font-mono" />
              <button onClick={deleteOld} disabled={phase === 'deleting' || confirmName !== item.name}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg px-3 py-1.5 disabled:opacity-40">
                {phase === 'deleting' && <RefreshCw className="animate-spin" style={{ width: 12, height: 12 }} />} Delete old bucket
              </button>
            </div>
          </div>
        </div>
      )}

      {err && <div className="text-[12px] text-rose-600 bg-rose-50 rounded-lg px-3 py-2 mt-2.5">{err}</div>}
    </div>
  );
}

// ── Dialog: Add S3 Bucket (admin only) ───────────────────────────────────────
const S3_REGIONS = [
  ['ap-southeast-1', 'Asia Pacific (Singapore)'],
  ['ap-southeast-2', 'Asia Pacific (Sydney)'],
  ['ap-southeast-3', 'Asia Pacific (Jakarta)'],
  ['us-east-1', 'US East (N. Virginia)'],
  ['us-west-2', 'US West (Oregon)'],
  ['eu-west-1', 'Europe (Ireland)'],
];

function S3CreateBucketModal({ buOptions = [], onClose, onCreated }) {
  const [name, setName] = useState('');
  const [region, setRegion] = useState('ap-southeast-1');
  const [bu, setBu] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [warn, setWarn] = useState(null);

  const nameOk = /^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/.test(name) && !name.includes('..');

  const submit = async () => {
    if (!nameOk) { setErr('Bucket name must be 3-63 characters: lowercase letters, numbers, dots and hyphens.'); return; }
    setBusy(true); setErr(null); setWarn(null);
    try {
      const r = await api.post('/api/aws-inventory/s3', { name, region, businessUnit: bu.trim() });
      onCreated && onCreated(r.data.bucket);
      if ((r.data.warnings || []).length) { setWarn(r.data.warnings.join(' ')); setBusy(false); return; }
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={busy ? undefined : onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <h3 className="font-display font-bold text-slate-800 flex items-center gap-2">
            <Boxes style={{ width: 17, height: 17 }} className="text-brand-500" /> Add S3 Bucket
          </h3>
          <button onClick={onClose} disabled={busy} className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"><X style={{ width: 16, height: 16 }} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Bucket name</label>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value.trim())}
              onKeyDown={(e) => { if (e.key === 'Enter' && !busy) submit(); }}
              placeholder="e.g. pil-ind-backup-archive" disabled={busy}
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none font-mono" />
            <p className={`text-[11px] mt-1 ${name && !nameOk ? 'text-rose-500' : 'text-slate-400'}`}>
              3-63 characters, lowercase letters, numbers, dots and hyphens. Must be globally unique.
            </p>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Region</label>
            <select value={region} onChange={(e) => setRegion(e.target.value)} disabled={busy}
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-brand-400 outline-none cursor-pointer">
              {S3_REGIONS.map(([v, l]) => <option key={v} value={v}>{v} — {l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Business Unit <span className="normal-case font-normal text-slate-400">(optional tag)</span></label>
            <input list="s3-bu-options" value={bu} onChange={(e) => setBu(e.target.value)} disabled={busy}
              placeholder="Used to group the bucket in this table"
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none" />
            <datalist id="s3-bu-options">{buOptions.map((b) => <option key={b} value={b} />)}</datalist>
          </div>
          <p className="text-[11px] text-slate-400">The bucket is created with AWS defaults (private, Block Public Access on).</p>
          {err && <div className="text-[12px] text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{err}</div>}
          {warn && <div className="text-[12px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2">{warn}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-slate-100">
          <button onClick={onClose} disabled={busy} className="text-[13px] font-medium text-slate-500 hover:text-slate-700 px-3 py-1.5">{warn ? 'Close' : 'Cancel'}</button>
          {!warn && (
            <button onClick={submit} disabled={busy || !name}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-lg px-4 py-1.5 disabled:opacity-50">
              {busy && <RefreshCw className="animate-spin" style={{ width: 13, height: 13 }} />} Create bucket
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Detail AMI ────────────────────────────────────────────────────────────────
function AmiDetail({ item, snapshots = [], onClose, locale, id }) {
  const {
    t
  } = useLang();

  const fmtTime = (t) => (t ? new Date(t).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
  const details = [
    ['AMI ID', item.id], ['Name', item.name || '—'], ['State', item.state],
    ['Architecture', item.architecture], ['Creation Date', item.creationDate],
    ['Region', item.region],
  ];
  const snapRows = snapshots.map((s) => [
    s.id, s.volumeId || '—', s.sizeGiB != null ? `${s.sizeGiB} GiB` : '—',
    s.state || '—', fmtTime(s.startTime), s.description || '—',
  ]);
  const tagRows = (item.tags || []).map((t) => [t.key, t.value]);

  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: ImageIcon,
      render: () => <KV items={details} locale={locale} /> },
    { key: 'snapshots', label: `Snapshots (${snapshots.length})`, icon: Camera,
      render: () => <MiniTable head={['Snapshot ID', 'Volume', 'Size', 'State', 'Started', 'Description']}
        rows={snapRows} empty={t('awsInv.noRelatedSnapshots')} /> },
    { key: 'tags', label: 'Tags', icon: Tag,
      render: () => <EditableTags resourceId={item.id || item.name} resourceType={item._rtype || 'ec2'} region={item.region || 'ap-southeast-1'} initialTags={item.tags || []} id={id} /> },
  ];

  return <DetailShell icon={ImageIcon} title={item.name || item.id} idText={item.id}
    badge={item.state} badgeClass={stateBadge(item.state)}
    sub={`${item.architecture || ''} · ${item.region}`}
    tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail VPN Connection (Site-to-Site) ──────────────────────────────────────
function VpnConnDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const details = [
    ['VPN Connection ID', item.id], ['Name', item.name || '—'],
    ['State', item.state], ['Type', item.type], ['Category', item.category],
    ['Customer Gateway', item.customerGatewayId], ['VPN Gateway', item.vpnGatewayId],
    ['Transit Gateway', item.transitGatewayId], ['Static Routes Only', item.staticRoutesOnly],
    ['Region', item.region],
  ];
  // Status tunnel live dari AWS (bukan snapshot) — di-fetch saat panel dibuka.
  const [live, setLive] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveErr, setLiveErr] = useState('');
  const loadLive = useCallback(async (force) => {
    if (!item || !item.id || !item.region) return;
    setLiveLoading(true); setLiveErr('');
    try {
      const params = { id: item.id, region: item.region };
      if (force) params.force = 1;
      const res = await api.get('/api/aws-inventory/vpn-tunnels', { params });
      setLive(res.data);
    } catch (e) {
      setLiveErr((e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'Request failed');
    } finally {
      setLiveLoading(false);
    }
  }, [item && item.id, item && item.region]);
  useEffect(() => { setLive(null); loadLive(false); }, [loadLive]);
  const hhmm = (d) => { const x = new Date(d); return `${String(x.getHours()).padStart(2, '0')}:${String(x.getMinutes()).padStart(2, '0')}`; };
  const liveLabel = liveLoading ? 'Fetching live status…'
    : live ? `Live · fetched ${hhmm(live.fetchedAt)}${live.cached ? ' (cached)' : ''}`
    : liveErr ? `Snapshot data · live fetch failed: ${liveErr}` : 'Snapshot data';

  const tunnelRows = ((live && live.tunnels) || item.tunnels || []).map((t) => [
    t.outsideIp || '—', t.status || '—', String(t.acceptedRouteCount ?? 0),
    t.statusMessage || '—',
    t.lastStatusChange ? new Date(t.lastStatusChange).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : '—',
  ]);
  const routeRows = (item.routes || []).map((r) => [r.cidr || '—', r.source || '—', r.state || '—']);
  const tagRows   = (item.tags || []).map((t) => [t.key, t.value]);

  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: Network,
      render: () => <KV items={details} locale={locale} /> },
    { key: 'tunnels', label: 'Tunnels', icon: ShieldCheck,
      render: () => <div>
        <div className="flex items-center justify-between gap-2 mb-2 text-xs">
          <span className={live ? 'text-emerald-600 font-medium' : (liveErr ? 'text-amber-600' : 'text-slate-500')}>{liveLabel}</span>
          <button type="button" onClick={() => loadLive(true)} disabled={liveLoading}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            <RefreshCw className={liveLoading ? 'animate-spin' : ''} style={{ width: 13, height: 13 }} /> Refresh
          </button>
        </div>
        <MiniTable head={['Outside IP', 'Status', 'Routes', 'Message', 'Last Changed']}
          rows={tunnelRows} empty={t('awsInv.noTunnels')} />
      </div> },
    { key: 'routes', label: 'Routes', icon: Globe2,
      render: () => <MiniTable head={['CIDR', 'Source', 'State']}
        rows={routeRows} empty={t('awsInv.noRoutes')} /> },
    { key: 'tags', label: 'Tags', icon: Tag,
      render: () => <EditableTags resourceId={item.id || item.name} resourceType={item._rtype || 'ec2'} region={item.region || 'ap-southeast-1'} initialTags={item.tags || []} id={id} /> },
  ];

  return <DetailShell icon={Network} title={item.name || item.id} idText={item.id}
    badge={item.state} badgeClass={stateBadge(item.state)}
    sub={`${item.type || ''} · ${item.region}`} tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail Customer Gateway ───────────────────────────────────────────────────
function CustomerGwDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const details = [
    ['Customer Gateway ID', item.id], ['Name', item.name || '—'],
    ['State', item.state], ['Type', item.type],
    ['IP Address', item.ipAddress], ['BGP ASN', item.bgpAsn],
    ['Device Name', item.deviceName], ['Certificate ARN', item.certificateArn],
    ['Region', item.region],
  ];
  const tagRows = (item.tags || []).map((t) => [t.key, t.value]);

  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: Globe2,
      render: () => <KV items={details} locale={locale} /> },
    { key: 'tags', label: 'Tags', icon: Tag,
      render: () => <EditableTags resourceId={item.id || item.name} resourceType={item._rtype || 'ec2'} region={item.region || 'ap-southeast-1'} initialTags={item.tags || []} id={id} /> },
  ];

  return <DetailShell icon={Globe2} title={item.name || item.id} idText={item.id}
    badge={item.state} badgeClass={stateBadge(item.state)}
    sub={`${item.type || ''} · IP: ${item.ipAddress || '—'} · ${item.region}`}
    tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail Virtual Private Gateway ───────────────────────────────────────────
function VpnGwDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const details = [
    ['VPN Gateway ID', item.id], ['Name', item.name || '—'],
    ['State', item.state], ['Type', item.type],
    ['Amazon Side ASN', item.amazonSideAsn],
    ['Availability Zone', item.availabilityZone],
    ['Region', item.region],
  ];
  const attRows = (item.attachments || []).map((a) => [a.vpcId || '—', a.state || '—']);
  const tagRows = (item.tags || []).map((t) => [t.key, t.value]);

  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: ShieldCheck,
      render: () => <KV items={details} locale={locale} /> },
    { key: 'attachments', label: 'VPC Attachments', icon: Network,
      render: () => <MiniTable head={['VPC ID', 'State']}
        rows={attRows} empty={t('awsInv.noAttachments')} /> },
    { key: 'tags', label: 'Tags', icon: Tag,
      render: () => <EditableTags resourceId={item.id || item.name} resourceType={item._rtype || 'ec2'} region={item.region || 'ap-southeast-1'} initialTags={item.tags || []} id={id} /> },
  ];

  return <DetailShell icon={ShieldCheck} title={item.name || item.id} idText={item.id}
    badge={item.state} badgeClass={stateBadge(item.state)}
    sub={`ASN: ${item.amazonSideAsn || '—'} · ${item.region}`}
    tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail AWS Config Rule ────────────────────────────────────────────────────
function ConfigDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const compClass = stateBadge(item.compliance);
  const details = [
    ['Rule Name', item.name], ['Rule ID', item.ruleId], ['State', item.state],
    ['Source', item.source], ['Source ID', item.sourceId],
    ['Scope', item.scope], ['Description', item.description],
    ['Created By', item.createdBy], ['Region', item.region],
  ];
  const compliance = [
    ['Status', item.compliance],
    ['Compliant Resources', item.compliantCount ?? '—'],
    ['Non-Compliant Resources', item.nonCompliantCount ?? '—'],
  ];

  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: ShieldCheck, render: () => (
      <>
        <div className="flex items-center gap-2 mb-4 p-3 rounded-xl bg-slate-50">
          <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${compClass}`}>{item.compliance}</span>
          {item.nonCompliantCount > 0 && (
            <span className="text-[12px] text-rose-600 font-medium">{item.nonCompliantCount} non-compliant resource</span>
          )}
          {item.compliantCount > 0 && (
            <span className="text-[12px] text-emerald-600 font-medium">{item.compliantCount} compliant</span>
          )}
        </div>
        <KV items={details} locale={locale} />
      </>
    ) },
    { key: 'compliance', label: 'Compliance', icon: Bell, render: () => <KV items={compliance} locale={locale} /> },
  ];

  return <DetailShell icon={ShieldCheck} title={item.name} idText={item.ruleId}
    badge={item.compliance} badgeClass={compClass}
    sub={`${item.sourceId || ''} · ${item.region}`}
    tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail Security Hub Finding ───────────────────────────────────────────────
function SecurityHubDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const sevClass = stateBadge(item.severity);
  const details = [
    ['Title', item.title], ['Severity', item.severity],
    ['Product', item.productName], ['Compliance', item.compliance],
    ['Resource Type', item.resourceType], ['Resource ID', item.resourceId],
    ['Created', item.createdAt], ['Updated', item.updatedAt], ['Region', item.region],
  ];

  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: ShieldCheck, render: () => (
      <>
        {item.description && (
          <div className="mb-4 p-3 bg-slate-50 rounded-xl text-[12px] text-slate-600 leading-relaxed">{item.description}</div>
        )}
        <KV items={details} locale={locale} />
        {item.remediationUrl && (
          <div className="mt-4 pt-4 border-t border-slate-50">
            <a href={item.remediationUrl} target="_blank" rel="noopener noreferrer"
              className="text-[12px] text-brand-600 hover:underline font-medium">
              🔗 {t('awsInv.remediationGuide')} ↗
            </a>
          </div>
        )}
      </>
    ) },
  ];

  return <DetailShell icon={ShieldCheck} title={item.title || item.id}
    idText={item.productName} badge={item.severity} badgeClass={sevClass}
    sub={`${item.resourceType || ''} · ${item.region}`}
    tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail Glacier Vault ──────────────────────────────────────────────────────
function GlacierDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const details = [
    ['Vault Name', item.name], ['ARN', item.arn],
    ['Number of Archives', item.numberOfArchives],
    ['Size', item.sizeGiB != null ? `${item.sizeGiB} GiB` : '—'],
    ['Size (bytes)', item.sizeInBytes?.toLocaleString() || '—'],
    ['Created', item.creationDate],
    ['Last Inventory', item.lastInventoryDate || (t('awsInv.never'))],
    ['Region', item.region],
  ];

  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: Boxes, render: () => <KV items={details} locale={locale} /> },
    { key: 'tags', label: 'Tags', icon: Tag, render: () =>
      <EditableTags resourceId={item.name} resourceType="glacier" region={item.region} initialTags={item.tags || []} id={id} /> },
  ];

  return <DetailShell icon={Boxes} title={item.name} idText={item.region}
    sub={`${item.numberOfArchives} archives · ${item.sizeGiB} GiB`}
    tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail EBS Snapshot ───────────────────────────────────────────────────────
function SnapshotDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const details = [
    ['Snapshot ID', item.id], ['Volume ID', item.volumeId],
    ['Size', item.sizeGiB != null ? `${item.sizeGiB} GiB *` : '—'],
    ['State', item.state], ['Started', item.startTime],
    ['Description', item.description], ['Region', item.region],
  ];
  const tagRows = (item.tags || []).map((t) => [t.key, t.value]);
  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: Camera, render: () => (
      <>
        <KV items={details} locale={locale} />
        <p className="mt-3 text-[10px] text-slate-400">* Ukuran volume asal, bukan actual snapshot size.</p>
      </>
    ) },
    { key: 'tags', label: 'Tags', icon: Tag, render: () =>
      <EditableTags resourceId={item.id} resourceType="ec2" region={item.region} initialTags={item.tags || []} id={id} /> },
  ];
  return <DetailShell icon={Camera} title={item.id} idText={item.volumeId}
    badge={item.state} badgeClass={stateBadge(item.state)} sub={`${item.sizeGiB} GiB · ${item.region}`}
    tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail VPC ────────────────────────────────────────────────────────────────
function VpcDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const details = [
    ['VPC ID', item.id], ['Name', item.name || '—'], ['CIDR', item.cidr],
    ['State', item.state], ['Default VPC', item.isDefault], ['Region', item.region],
  ];
  const tagRows = (item.tags || []).map((t) => [t.key, t.value]);
  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: Network, render: () => <KV items={details} locale={locale} /> },
    { key: 'tags', label: 'Tags', icon: Tag, render: () =>
      <EditableTags resourceId={item.id} resourceType="ec2" region={item.region} initialTags={item.tags || []} id={id} /> },
  ];
  return <DetailShell icon={Network} title={item.name || item.id} idText={item.id}
    badge={item.state} badgeClass={stateBadge(item.state)} sub={`${item.cidr} · ${item.region}`}
    tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail Subnet ─────────────────────────────────────────────────────────────
function SubnetDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const details = [
    ['Subnet ID', item.id], ['Name', item.name || '—'], ['VPC ID', item.vpcId],
    ['CIDR', item.cidr], ['AZ', item.az], ['Available IPs', item.availableIps], ['Region', item.region],
  ];
  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: Network, render: () => <KV items={details} locale={locale} /> },
    { key: 'tags', label: 'Tags', icon: Tag, render: () =>
      <EditableTags resourceId={item.id} resourceType="ec2" region={item.region} initialTags={item.tags || []} id={id} /> },
  ];
  return <DetailShell icon={Network} title={item.name || item.id} idText={item.id}
    sub={`${item.cidr} · ${item.az}`} tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail Key Pair ───────────────────────────────────────────────────────────
function KeyPairDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const details = [
    ['Name', item.name], ['Key Pair ID', item.id], ['Type', item.type], ['Region', item.region],
  ];
  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: Key, render: () => <KV items={details} locale={locale} /> },
    { key: 'tags', label: 'Tags', icon: Tag, render: () =>
      <EditableTags resourceId={item.id} resourceType="ec2" region={item.region} initialTags={item.tags || []} id={id} /> },
  ];
  return <DetailShell icon={Key} title={item.name} idText={item.id}
    sub={`${item.type || '—'} · ${item.region}`} tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail Lambda Function ────────────────────────────────────────────────────
function LambdaDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const details = [
    ['Function Name', item.name], ['Runtime', item.runtime],
    ['Memory', item.memoryMB != null ? `${item.memoryMB} MB` : '—'],
    ['Code Size', item.codeSizeKB != null ? `${item.codeSizeKB} KB` : '—'],
    ['Last Modified', item.lastModified], ['Region', item.region],
  ];
  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: Zap, render: () => <KV items={details} locale={locale} /> },
    { key: 'tags', label: 'Tags', icon: Tag, render: () =>
      <EditableTags resourceId={item.name} resourceType="lambda" region={item.region} initialTags={item.tags || []} id={id} /> },
  ];
  return <DetailShell icon={Zap} title={item.name} idText={item.runtime}
    sub={`${item.memoryMB} MB · ${item.region}`} tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail CloudWatch Alarm ───────────────────────────────────────────────────
function AlarmDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const alarmBadge = item.state === 'OK' ? 'bg-emerald-100 text-emerald-700'
    : item.state === 'ALARM' ? 'bg-rose-100 text-rose-700'
    : 'bg-slate-200 text-slate-500';

  const fmtOp = (op) => {
    const m = { GreaterThanThreshold: '>', GreaterThanOrEqualToThreshold: '≥',
      LessThanThreshold: '<', LessThanOrEqualToThreshold: '≤',
      GreaterThanUpperThreshold: '> upper', LessThanLowerThreshold: '< lower' };
    return m[op] || op || '—';
  };

  const details = [
    ['Alarm Name', item.name],
    ['State', item.state],
    [t('awsInv.stateUpdated'), item.stateUpdated],
    [t('awsInv.stateReason'), item.stateReason],
    ['Metric', item.metric],
    ['Namespace', item.namespace],
    ['Statistic', item.statistic],
    ['Period', item.period != null ? `${item.period}s` : '—'],
    ['Threshold', item.threshold != null ? `${fmtOp(item.comparisonOperator)} ${item.threshold}` : '—'],
    [t('awsInv.evaluationPeriods'), item.evaluationPeriods],
    ['Datapoints to Alarm', item.datapointsToAlarm],
    [t('awsInv.treatMissingData'), item.treatMissingData],
    [t('awsInv.actionsEnabled'), item.actionsEnabled ? (t('awsInv.yes')) : (t('awsInv.no'))],
    ['Region', item.region],
  ];

  const dims = (item.dimensions || []);
  const actions = [
    ...(item.alarmActions || []).map((a) => ({ type: 'ALARM', arn: a })),
    ...(item.okActions || []).map((a) => ({ type: 'OK', arn: a })),
    ...(item.insufficientDataActions || []).map((a) => ({ type: 'INSUFFICIENT_DATA', arn: a })),
  ];

  // History tab — on-demand
  const [histState, setHistState] = useState('idle');
  const [history, setHistory]     = useState([]);
  const [histErr, setHistErr]     = useState(null);
  const fetchHistory = useCallback(async () => {
    setHistState('loading'); setHistErr(null);
    try {
      const res = await api.get(`/api/aws-inventory/cw/alarm/${encodeURIComponent(item.name)}/history?region=${item.region || 'ap-southeast-1'}`);
      setHistory(res.data); setHistState('done');
    } catch (e) { setHistErr(e.response?.data?.error || e.message); setHistState('error'); }
  }, [item.name, item.region]);

  const typeColor = (t) => t === 'StateUpdate' ? 'text-brand-600' : t === 'Action' ? 'text-amber-600' : 'text-slate-500';

  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: Bell, render: () => (
      <div className="space-y-4">
        <KV items={details} locale={locale} />
        {dims.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Dimensions</p>
            <MiniTable head={['Name', 'Value']} rows={dims.map((d) => [d.name, d.value])} empty="—" />
          </div>
        )}
        {actions.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Actions</p>
            <div className="space-y-1">
              {actions.map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-[11px] bg-slate-50 rounded-lg px-3 py-1.5">
                  <span className={`font-semibold flex-shrink-0 ${a.type === 'ALARM' ? 'text-rose-600' : a.type === 'OK' ? 'text-emerald-600' : 'text-slate-400'}`}>{a.type}</span>
                  <span className="text-slate-500 font-mono break-all">{a.arn}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    ) },
    { key: 'history', label: t('awsInv.history'), icon: RefreshCw, onActivate: fetchHistory, render: () => {
      if (histState === 'idle')    return <p className="text-[12px] text-slate-400">{t('awsInv.clickTabLoadHistory')}</p>;
      if (histState === 'loading') return <div className="flex items-center gap-2 text-slate-400 py-3"><RefreshCw className="animate-spin" style={{ width: 14, height: 14 }} /><span className="text-[12px]">Loading…</span></div>;
      if (histState === 'error')   return <div className="text-[12px] text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{histErr}</div>;
      if (!history.length) return <p className="text-[12px] text-slate-400">{t('awsInv.noHistoryFound')}</p>;
      return (
        <div className="space-y-2">
          {history.map((h, i) => (
            <div key={i} className="flex gap-3 text-[12px] border-b border-slate-50 pb-2">
              <span className="text-slate-400 flex-shrink-0 w-32">{h.timestamp ? new Date(h.timestamp).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span>
              <span className={`font-semibold flex-shrink-0 w-24 ${typeColor(h.type)}`}>{h.type}</span>
              <span className="text-slate-600 leading-relaxed">{h.summary}</span>
            </div>
          ))}
        </div>
      );
    } },
    { key: 'tags', label: 'Tags', icon: Tag, render: () =>
      <EditableTags resourceId={item.arn} resourceType="cloudwatch" region={item.region} initialTags={item.tags || []} id={id} /> },
  ];

  return <DetailShell icon={Bell} title={item.name} idText={item.namespace}
    badge={item.state} badgeClass={alarmBadge} sub={`${item.metric} · ${item.region}`}
    tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail IAM User ───────────────────────────────────────────────────────────
// Panel daftar policy (managed + inline)
function PolicyList({ attached = [], inline = [], id }) {
  const {
    t
  } = useLang();

  if (!attached.length && !inline.length)
    return <p className="text-[12px] text-slate-400">{t('awsInv.noPoliciesAttached')}</p>;
  return (
    <div className="space-y-1.5">
      {attached.map((p) => (
        <div key={p.arn || p.name} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
          <ShieldCheck style={{ width: 13, height: 13 }} className="text-brand-500 flex-shrink-0" />
          <span className="text-[12px] font-medium text-slate-700 flex-1 truncate">{p.name}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-100 text-brand-600 font-semibold">{p.type}</span>
        </div>
      ))}
      {inline.map((p) => (
        <div key={p.name} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
          <ShieldCheck style={{ width: 13, height: 13 }} className="text-slate-400 flex-shrink-0" />
          <span className="text-[12px] font-medium text-slate-700 flex-1 truncate">{p.name}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 font-semibold">{p.type}</span>
        </div>
      ))}
    </div>
  );
}

// Panel Last Access (Access Advisor) — fetch on-demand karena lambat
function LastAccessPanel({ endpoint, locale, id }) {
  const {
    t
  } = useLang();

  const [state, setState] = useState('idle');
  const [data, setData]   = useState(null);
  const [err, setErr]     = useState(null);
  const fetchData = useCallback(async () => {
    setState('loading'); setErr(null);
    try {
      const res = await api.get(endpoint);
      setData(res.data); setState('done');
    } catch (e) { setErr(e.response?.data?.error || e.message); setState('error'); }
  }, [endpoint]);

  if (state === 'idle')
    return (
      <button onClick={fetchData} className="text-[12px] font-medium text-brand-600 hover:text-brand-700">
        {t('awsInv.loadLastAccessDataAccess')}
      </button>
    );
  if (state === 'loading')
    return <div className="flex items-center gap-2 text-slate-400 py-3"><RefreshCw className="animate-spin" style={{ width: 14, height: 14 }} /><span className="text-[12px]">{t('awsInv.analyzingServiceAccess')}</span></div>;
  if (state === 'error')
    return <div className="text-[12px] text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{err}</div>;
  if (data?.status === 'in_progress')
    return <div className="text-[12px] text-amber-600">{t('awsInv.analysisStillRunningTryAgain')} <button onClick={fetchData} className="underline ml-1">retry</button></div>;
  const rows = (data?.services || []).map((s) => [
    s.service, s.lastAccessed ? new Date(s.lastAccessed).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : '—', String(s.authenticatedEntities),
  ]);
  return (
    <MiniTable head={['Service', t('awsInv.lastAccessed'), t('awsInv.entities')]} rows={rows}
      empty={t('awsInv.noServiceAccessRecorded')} />
  );
}

// ── Detail IAM User ───────────────────────────────────────────────────────────
function IamUserDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const [state, setState]   = useState('loading');
  const [data, setData]     = useState(null);
  const [err, setErr]       = useState(null);
  // Reset password
  const [resetting, setResetting] = useState(false);
  const [newPwd, setNewPwd]       = useState(null);
  const [resetErr, setResetErr]   = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    setState('loading'); setErr(null);
    try {
      const res = await api.get(`/api/aws-inventory/iam/user/${encodeURIComponent(item.name)}`);
      setData(res.data); setState('done');
    } catch (e) { setErr(e.response?.data?.error || e.message); setState('error'); }
  }, [item.name]);
  useEffect(() => { load(); }, [load]);

  const doReset = useCallback(async () => {
    if (!window.confirm(t('awsInv.resetConsolePasswordNameNew', {
      name: item.name
    }))) return;
    setResetting(true); setResetErr(null); setNewPwd(null);
    try {
      const res = await api.post('/api/aws-inventory/iam/reset-password', { username: item.name });
      setNewPwd(res.data.password);
    } catch (e) { setResetErr(e.response?.data?.error || e.message); }
    finally { setResetting(false); }
  }, [item.name, id]);

  const fmt = (t) => (t ? new Date(t).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : (id ? 'Tidak pernah' : 'Never'));

  const loadingPanel = <div className="flex items-center gap-2 text-slate-400 py-4"><RefreshCw className="animate-spin" style={{ width: 14, height: 14 }} /><span className="text-[12px]">Loading…</span></div>;

  const detailsTab = () => {
    if (state === 'loading') return loadingPanel;
    if (state === 'error') return <div className="text-[12px] text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{err}</div>;
    return <KV items={[
      ['Username', data.username], ['User ID', data.userId],
      ['ARN', data.arn], ['Created', data.created],
    ]} locale={locale} />;
  };

  const credentialsTab = () => {
    if (state === 'loading') return loadingPanel;
    if (state === 'error') return <div className="text-[12px] text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{err}</div>;
    return (
      <div className="space-y-4">
        {/* Ringkasan status */}
        <div className="grid grid-cols-2 gap-2">
          <StatusCard label="Console Password" ok={data.hasConsolePassword}
            okText={t('awsInv.enabled')} noText={t('awsInv.none')} />
          <StatusCard label="MFA" ok={data.mfaEnabled}
            okText={t('awsInv.enabled')} noText={t('awsInv.disabled')} warnIfNo />
        </div>
        <KV items={[
          [t('awsInv.consoleLastLogin'), fmt(data.passwordLastUsed)],
          ['MFA Devices', data.mfaDevices.length ? data.mfaDevices.map((d) => d.serial).join(', ') : (t('awsInv.none'))],
        ]} locale={locale} />
        {/* Access keys */}
        <div>
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Access Keys</p>
          <MiniTable head={['Key ID', 'Status', t('awsInv.created'), t('awsInv.lastUsed')]}
            rows={data.accessKeys.map((k) => [
              k.accessKeyId,
              k.status,
              k.created ? new Date(k.created).toLocaleDateString(locale) : '—',
              k.lastUsed ? `${new Date(k.lastUsed).toLocaleDateString(locale)}${k.lastUsedService ? ` (${k.lastUsedService})` : ''}` : (t('awsInv.never')),
            ])}
            empty={t('awsInv.noAccessKeys')} />
        </div>
        {/* Reset password */}
        {isAdmin && (
          <div className="pt-3 border-t border-slate-100">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">{t('awsInv.resetConsolePassword')}</p>
            {newPwd ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-[11px] text-emerald-700 font-medium mb-1.5">{t('awsInv.newPasswordCopyShareSecurely')}</p>
                <code className="block text-[13px] font-mono bg-white border border-emerald-200 rounded px-2 py-1.5 text-slate-800 select-all break-all">{newPwd}</code>
                <p className="text-[10px] text-emerald-600 mt-1.5">{t('awsInv.userMustChangePasswordFirst')}</p>
              </div>
            ) : (
              <>
                <button onClick={doReset} disabled={resetting}
                  className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:bg-slate-200 disabled:text-slate-400 text-white transition-colors">
                  {resetting ? (t('awsInv.resetting')) : (t('awsInv.resetPasswordRandom'))}
                </button>
                {resetErr && <p className="text-[11px] text-rose-600 mt-1.5">{resetErr}</p>}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: Users, render: detailsTab },
    { key: 'policies', label: t('awsInv.permissions'), icon: ShieldCheck, render: () =>
      state === 'done' ? <PolicyList attached={data.attachedPolicies} inline={data.inlinePolicies} id={id} /> : (state === 'loading' ? loadingPanel : <span className="text-[12px] text-rose-600">{err}</span>) },
    { key: 'groups', label: t('awsInv.groups'), icon: Users, render: () =>
      state === 'done'
        ? <MiniTable head={[t('awsInv.groupName'), 'ARN']} rows={data.groups.map((g) => [g.name, g.arn])} empty={t('awsInv.notMemberAnyGroup')} />
        : (state === 'loading' ? loadingPanel : <span className="text-[12px] text-rose-600">{err}</span>) },
    { key: 'credentials', label: t('awsInv.securityCredentials'), icon: Key, render: credentialsTab },
    { key: 'lastaccess', label: 'Last Access', icon: ShieldCheck, render: () =>
      <LastAccessPanel endpoint={`/api/aws-inventory/iam/user/${encodeURIComponent(item.name)}/last-access`} locale={locale} id={id} /> },
  ];

  return <DetailShell icon={Users} title={item.name} idText={item.id}
    sub={item.arn} tabs={tabs} onClose={onClose} id={id} />;
}

// ── Detail IAM Role ───────────────────────────────────────────────────────────
function IamRoleDetail({ item, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const [state, setState] = useState('loading');
  const [data, setData]   = useState(null);
  const [err, setErr]     = useState(null);

  const load = useCallback(async () => {
    setState('loading'); setErr(null);
    try {
      const res = await api.get(`/api/aws-inventory/iam/role/${encodeURIComponent(item.name)}`);
      setData(res.data); setState('done');
    } catch (e) { setErr(e.response?.data?.error || e.message); setState('error'); }
  }, [item.name]);
  useEffect(() => { load(); }, [load]);

  const fmt = (t) => (t ? new Date(t).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : (id ? 'Tidak pernah' : 'Never'));
  const loadingPanel = <div className="flex items-center gap-2 text-slate-400 py-4"><RefreshCw className="animate-spin" style={{ width: 14, height: 14 }} /><span className="text-[12px]">Loading…</span></div>;

  const detailsTab = () => {
    if (state === 'loading') return loadingPanel;
    if (state === 'error') return <div className="text-[12px] text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{err}</div>;
    return (
      <KV items={[
        ['Role Name', data.rolename], ['Role ID', data.roleId], ['ARN', data.arn],
        ['Created', data.created], ['Description', data.description || '—'],
        [t('awsInv.maxSession'), data.maxSessionDuration ? `${data.maxSessionDuration}s` : '—'],
        [t('awsInv.roleLastUsed'), fmt(data.roleLastUsed)],
      ]} locale={locale} />
    );
  };

  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: ShieldCheck, render: detailsTab },
    { key: 'policies', label: t('awsInv.permissions'), icon: ShieldCheck, render: () =>
      state === 'done' ? <RolePolicyBlock roleName={data.rolename} roleArn={data.arn} attached={data.attachedPolicies} inline={data.inlinePolicies} onChanged={load} showHeader={false} /> : (state === 'loading' ? loadingPanel : <span className="text-[12px] text-rose-600">{err}</span>) },
    { key: 'lastaccess', label: 'Last Access', icon: ShieldCheck, render: () =>
      <LastAccessPanel endpoint={`/api/aws-inventory/iam/role/${encodeURIComponent(item.name)}/last-access`} locale={locale} id={id} /> },
  ];

  return <DetailShell icon={ShieldCheck} title={item.name} idText={item.id}
    sub={item.arn} tabs={tabs} onClose={onClose} id={id} />;
}

// Status card kecil (hijau OK / merah-amber tidak)
function StatusCard({ label, ok, okText, noText, warnIfNo }) {
  const cls = ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : warnIfNo ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-slate-50 text-slate-500 border-slate-200';
  return (
    <div className={`rounded-lg border px-3 py-2 ${cls}`}>
      <p className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</p>
      <p className="text-[13px] font-bold mt-0.5">{ok ? okText : noText}</p>
    </div>
  );
}

// ── Detail Elastic IP ─────────────────────────────────────────────────────────
function EipDetail({ item, instNameMap = {}, onClose, locale, id }) {
  const {
    t
  } = useLang();

  const instanceName = item.instanceId ? (instNameMap[item.instanceId] || item.instanceId) : null;
  const assocBadge = item.associated
    ? 'bg-emerald-100 text-emerald-700'
    : 'bg-amber-100 text-amber-700';

  const details = [
    ['Public IP',         item.publicIp],
    ['Allocation ID',     item.allocationId],
    ['Domain',            item.domain],
    [id ? 'Status' : 'Status', item.associated ? (t('awsInv.associated')) : (t('awsInv.notAssociated'))],
    ['Association ID',    item.associationId || '—'],
    [t('awsInv.associatedInstance'), item.instanceId
      ? `${instanceName}${instanceName !== item.instanceId ? ` (${item.instanceId})` : ''}`
      : '—'],
    ['Private IP',        item.privateIp || '—'],
    ['Network Interface', item.networkInterfaceId || '—'],
    ['Region',            item.region],
  ];

  const tabs = [
    { key: 'details', label: t('awsInv.details'), icon: Globe2, render: () => (
      <>
        {/* Info instance terhubung — highlight kalau ada */}
        {item.instanceId && (
          <div className="mb-4 flex items-center gap-3 p-3 bg-brand-50 rounded-xl border border-brand-100">
            <Server style={{ width: 15, height: 15 }} className="text-brand-500 flex-shrink-0" />
            <div>
              <p className="text-[11px] font-semibold text-brand-700 uppercase tracking-wide">{t('awsInv.associatedInstance')}</p>
              <p className="text-[13px] font-bold text-slate-800">{instanceName}</p>
              {instanceName !== item.instanceId && (
                <p className="text-[11px] font-mono text-slate-400">{item.instanceId}</p>
              )}
            </div>
          </div>
        )}
        {!item.instanceId && (
          <div className="mb-4 flex items-center gap-2 p-3 bg-amber-50 rounded-xl border border-amber-100 text-amber-700 text-[12px] font-medium">
            <AlertTriangle style={{ width: 14, height: 14 }} />
            {t('awsInv.eipNotAssociatedAnyInstance')}
          </div>
        )}
        <KV items={details} locale={locale} />
      </>
    ) },
    { key: 'tags', label: 'Tags', icon: Tag, render: () =>
      <EditableTags resourceId={item.allocationId} resourceType="ec2" region={item.region} initialTags={item.tags || []} id={id} /> },
  ];

  return (
    <DetailShell icon={Globe2} title={item.name || item.publicIp} idText={item.allocationId}
      badge={item.associated ? (t('awsInv.associated')) : (id ? 'Idle' : 'Idle')}
      badgeClass={assocBadge}
      sub={`${item.publicIp} · ${item.domain} · ${item.region}`}
      tabs={tabs} onClose={onClose} id={id} />
  );
}
